import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Compass, Coffee, Flame, Heart, Mail, Map, Star, Sparkles, MessageSquare, Trees, ArrowDown, Wifi, ArrowRight, CheckCircle, Calendar, Users, Send, ShieldAlert, Phone, Shield, Wind, Utensils, Shirt, Briefcase, Clock, Instagram, Twitter, MessageCircle, Lock, Settings, Menu, X, ChevronDown, Footprints, Milestone, ShieldCheck, Percent, Bell, AlertTriangle, CreditCard, CigaretteOff, UserX, Ban, UserMinus, Moon, Sun, ChevronLeft, ChevronRight, Droplet, Beer, Camera } from 'lucide-react';
import { translations } from './utils/translations';
import { resolveMediaUrl } from './utils/mediaHelper';
import { fallbackReviews, fallbackRooms } from './utils/fallbackData';
import { hydrateStorageFromServer, safeLocalStorage } from './utils/storageWrapper';
import RoomCard from './components/RoomCard';
import BookingForm from './components/BookingForm';
import RobustImage from './components/RobustImage';
import RobustBackgroundSection from './components/RobustBackgroundSection';

// Lazy-loaded components for accelerated initial load times & smaller bundle size
const BookingModal = React.lazy(() => import('./components/BookingModal'));
const CozzyBusinessMap = React.lazy(() => import('./components/CozzyBusinessMap'));
const LanguageTranslator = React.lazy(() => import('./components/LanguageTranslator'));
const ConciergeBot = React.lazy(() => import('./components/ConciergeBot'));
const FaqsPolicies = React.lazy(() => import('./components/FaqsPolicies'));
const PhotoGallery = React.lazy(() => import('./components/PhotoGallery'));
const MediaManager = React.lazy(() => import('./components/MediaManager'));
const PrivacyPolicyModal = React.lazy(() => import('./components/PrivacyPolicyModal'));

const kyivMaidanAerial = 'https://images.unsplash.com/photo-1561542320-9a18cd340469?auto=format&fit=crop&w=1200&q=60';
const kyivMystetskyiArsenal = 'https://images.unsplash.com/photo-1610123512209-fa929d2f2df3?auto=format&fit=crop&w=1200&q=60';
const kyivAndriyivskyyDescent = 'https://images.unsplash.com/photo-1579294800821-694d95e86143?auto=format&fit=crop&w=1200&q=80';
const kyivSquat17b = 'https://images.unsplash.com/photo-1614713459461-8408f9f1ff4f?auto=format&fit=crop&w=1200&q=60';
const cozzydineLogoMain = 'https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=120&h=120&q=60';
const cozzydineReceptionMain = 'https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=1000&q=60';

export default function App() {
  const getPageFromPath = (pathname: string): 'home' | 'rooms' | 'about' | 'contact' => {
    const cleanPath = pathname.replace(/\/$/, '') || '/';
    if (cleanPath === '/rooms-and-dorms' || cleanPath === '/rooms') return 'rooms';
    if (cleanPath === '/about' || cleanPath === '/about-us') return 'about';
    if (cleanPath === '/contact' || cleanPath === '/contact-us') return 'contact';
    return 'home';
  };

  const getPathFromPage = (page: 'home' | 'rooms' | 'about' | 'contact'): string => {
    switch (page) {
      case 'rooms': return '/rooms-and-dorms';
      case 'about': return '/about';
      case 'contact': return '/contact';
      case 'home': default: return '/';
    }
  };

  const getBookingEngineDefaultDates = () => {
    try {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dayAfter = new Date(tomorrow);
      dayAfter.setDate(dayAfter.getDate() + 1);

      const formatDate = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      return {
        todayStr: formatDate(today),
        tomorrowStr: formatDate(tomorrow),
        dayAfterStr: formatDate(dayAfter)
      };
    } catch (e) {
      return {
        todayStr: '2026-06-24',
        tomorrowStr: '2026-06-25',
        dayAfterStr: '2026-06-26'
      };
    }
  };

  const { todayStr, tomorrowStr, dayAfterStr } = getBookingEngineDefaultDates();

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      return (safeLocalStorage.getItem('cozzydine-theme') as 'light' | 'dark') || 'dark';
    } catch (e) {
      return 'dark';
    }
  });

  useEffect(() => {
    try {
      safeLocalStorage.setItem('cozzydine-theme', theme);
    } catch (e) {}
  }, [theme]);

  // Non-blocking background image preload during browser idle time
  useEffect(() => {
    const urlsToPreload = [
      kyivMaidanAerial,
      kyivMystetskyiArsenal,
      kyivAndriyivskyyDescent,
      kyivSquat17b,
      cozzydineReceptionMain
    ];
    
    const preload = () => {
      urlsToPreload.forEach(url => {
        const img = new Image();
        img.src = url;
      });
    };

    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(preload, { timeout: 3000 });
      } else {
        setTimeout(preload, 1500);
      }
    }
  }, []);

  const [activePage, setActivePage] = useState<'home' | 'rooms' | 'about' | 'contact'>(() => {
    if (typeof window !== 'undefined') {
      return getPageFromPath(window.location.pathname);
    }
    return 'home';
  });

  const navigateTo = (page: 'home' | 'rooms' | 'about' | 'contact', replace = false) => {
    const targetPath = getPathFromPage(page);
    const searchAndHash = window.location.search + window.location.hash;
    const fullUrl = targetPath + searchAndHash;

    if (replace) {
      window.history.replaceState({ page }, '', fullUrl);
    } else if (window.location.pathname !== targetPath) {
      window.history.pushState({ page }, '', fullUrl);
    }

    setActivePage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handlePopState = () => {
      const page = getPageFromPath(window.location.pathname);
      setActivePage(page);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const initialPage = getPageFromPath(window.location.pathname);
      const targetPath = getPathFromPage(initialPage);
      if (window.location.pathname !== targetPath) {
        window.history.replaceState({ page: initialPage }, '', targetPath + window.location.search + window.location.hash);
      } else {
        window.history.replaceState({ page: initialPage }, '', window.location.href);
      }
    }
  }, []);

  const [isMediaCabinetOpen, setIsMediaCabinetOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [imageVersion, setImageVersion] = useState('1.1');
  const [activeFaq, setActiveFaq] = useState<number | null>(0);
  const [activeTransitTab, setActiveTransitTab] = useState<'lybidska' | 'palats' | 'train' | 'bus' | 'centralBus'>('lybidska');
  const [selectedPolicyCategory, setSelectedPolicyCategory] = useState<'all' | 'stay' | 'facilities' | 'rules'>('all');

  const [sightseeingIndex, setSightseeingIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const getInitialViewportScale = () => {
    if (typeof window !== 'undefined') {
      const parentWidth = window.innerWidth || document.documentElement.clientWidth || (window.screen ? window.screen.width : 0);
      const targetWidth = 1280;
      if (parentWidth && parentWidth < targetWidth) {
        return parentWidth / targetWidth;
      }
    }
    return 1;
  };

  const [viewportScale, setViewportScale] = useState<number>(getInitialViewportScale);
  const [scaledHeight, setScaledHeight] = useState<number | string>('auto');
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [configVersion, setConfigVersion] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('+380 66 207 0475');
  const [emailAddress, setEmailAddress] = useState('cozzydinekyiv@gmail.com');
  const [quietHours, setQuietHours] = useState('23:00 to 08:00');
  const [physicalAddress, setPhysicalAddress] = useState('Antonovycha 169/11, 75a, Kyiv, 03150');

  const [announcement, setAnnouncement] = useState<{
    active: boolean;
    messageEn: string;
    messageUk: string;
    type: 'promo' | 'notice' | 'danger' | 'nightly';
    dismissible: boolean;
  } | null>(null);
  const [isAnnouncementDismissed, setIsAnnouncementDismissed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [modalInitialStep, setModalInitialStep] = useState<number | null>(null);
  const [modalInitialConfirmedBooking, setModalInitialConfirmedBooking] = useState<any>(null);

  useEffect(() => {
    setIsMobile(false);
  }, []);

  useEffect(() => {
    const handleNavigateContact = () => {
      navigateTo('contact');
    };
    window.addEventListener('cozzydine-navigate-contact', handleNavigateContact);
    return () => {
      window.removeEventListener('cozzydine-navigate-contact', handleNavigateContact);
    };
  }, []);

  useEffect(() => {
    const handleNavigateAboutFaqs = () => {
      navigateTo('about');
      setTimeout(() => {
        const el = document.getElementById('faqs-accordion-section') || document.getElementById('faqs-policies-root');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        window.dispatchEvent(new CustomEvent('cozzydine-open-faq', { detail: { index: 3 } }));
      }, 150);
    };
    window.addEventListener('cozzydine-navigate-about-faqs', handleNavigateAboutFaqs);
    return () => {
      window.removeEventListener('cozzydine-navigate-about-faqs', handleNavigateAboutFaqs);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('privacy') === 'true' || window.location.hash === '#privacy') {
        setIsPrivacyOpen(true);
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
      }
    }
  }, []);// Immediate viewport scaling handler (synchronous before paint on mount, debounced on resize)
  useLayoutEffect(() => {
    const measure = () => {
      const parentWidth = window.innerWidth || document.documentElement.clientWidth;
      const targetWidth = 1280;
      const sc = parentWidth < targetWidth ? parentWidth / targetWidth : 1;
      setViewportScale(sc);
      
      if (contentRef.current) {
        const unscaledHeight = contentRef.current.scrollHeight;
        // Add a 50px buffer on mobile scaled view to ensure bottom footer descenders and margins are never clipped by overflowY: hidden
        setScaledHeight(sc < 1 ? (unscaledHeight + 50) * sc : 'auto');
      }
    };

    // Execute immediately on initial mount synchronously before browser paint!
    measure();

    let rafId: number | null = null;
    const handleResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    let resizeObserver: ResizeObserver | null = null;
    if (contentRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(contentRef.current);
    }

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const handleShowPolicy = (e: Event) => {
      const customEvent = e as CustomEvent<{ category: 'all' | 'stay' | 'facilities' | 'rules' }>;
      if (customEvent.detail && customEvent.detail.category) {
        setSelectedPolicyCategory(customEvent.detail.category);
      }
    };
    window.addEventListener('show-policy-category', handleShowPolicy);
    return () => {
      window.removeEventListener('show-policy-category', handleShowPolicy);
    };
  }, []);

  const loadConfigs = () => {
    try {
      const savedPhone = safeLocalStorage.getItem('cozzydine_cfg_phone');
      const savedEmail = safeLocalStorage.getItem('cozzydine_cfg_email');
      const savedQuiet = safeLocalStorage.getItem('cozzydine_cfg_quiet_hours');
      const savedAddress = safeLocalStorage.getItem('cozzydine_cfg_address');

      if (savedPhone) setPhoneNumber(savedPhone);
      if (savedEmail) setEmailAddress(savedEmail);
      if (savedQuiet) setQuietHours(savedQuiet);
      if (savedAddress) setPhysicalAddress(savedAddress);

      const savedNotice = safeLocalStorage.getItem('cozzydine_announcement');
      if (savedNotice) {
        const parsed = JSON.parse(savedNotice);
        setAnnouncement(parsed);
      } else {
        setAnnouncement(null);
      }
    } catch (e) {
      console.warn('Failed to load settings:', e);
    }
  };

  useEffect(() => {
    loadConfigs();
    setIsAnnouncementDismissed(false);
  }, [configVersion]);

  // Optimized non-blocking server synchronization breaking the critical request chain
  useEffect(() => {
    const handleInitialSync = async () => {
      try {
        const success = await hydrateStorageFromServer();
        if (success) {
          setConfigVersion(prev => prev + 1);
          setImageVersion(Date.now().toString());
        }
      } catch (err) {
        console.error('[Cozzydine] Error carrying out startup server synchronization:', err);
      }
    };

    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => {
          handleInitialSync();
        }, { timeout: 2500 });
      } else {
        setTimeout(handleInitialSync, 1200);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentSuccess = urlParams.get('payment_success');
        const bookingId = urlParams.get('booking_id');
        
        if (paymentSuccess === 'true' && bookingId) {
          const pendingRaw = safeLocalStorage.getItem('cozzydine_pending_liqpay_booking');
          if (pendingRaw) {
            const pendingBooking = JSON.parse(pendingRaw);
            
            if (pendingBooking.id === bookingId) {
              const expectedAmount = Number(pendingBooking.totalPrice);
              const paidAmountRaw = urlParams.get('amount');
              const paidAmount = paidAmountRaw ? parseFloat(paidAmountRaw) : 0;
              
              const isFullPayment = paidAmountRaw !== null && Math.abs(paidAmount - expectedAmount) < 0.01;
              
              if (!isFullPayment) {
                const finalBooking = {
                  ...pendingBooking,
                  status: 'rejected',
                  paymentId: `PAY-LIQPAY-GATEWAY-REJECTED-${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
                  rejectionReason: `Payment mismatch: Received ₴${paidAmount} but expected ₴${expectedAmount}. Booking NOT confirmed.`
                };
                
                const savedBookings = safeLocalStorage.getItem('cozzydine_bookings_v21');
                const bookingsList = savedBookings ? JSON.parse(savedBookings) : [];
                
                const exists = bookingsList.some((b: any) => b.id === bookingId);
                if (!exists) {
                  const updatedList = [finalBooking, ...bookingsList];
                  safeLocalStorage.setItem('cozzydine_bookings_v21', JSON.stringify(updatedList));
                }
                
                safeLocalStorage.removeItem('cozzydine_pending_liqpay_booking');
                
                const roomsRaw = safeLocalStorage.getItem('cozzydine_rooms_v21');
                const roomsList = roomsRaw ? JSON.parse(roomsRaw) : [];
                const roomObj = roomsList.find((r: any) => r.id === finalBooking.roomId) || {
                  id: finalBooking.roomId,
                  name: finalBooking.roomName,
                  price: finalBooking.totalPrice / 3,
                  type: 'boutique-pod',
                  description: 'Kyiv Boutique Pod Space',
                  features: ['Free Wifi', 'Safe shelter access'],
                  images: [],
                  beds: 1,
                  maxGuests: 2,
                  bathrooms: 'Shared',
                  space: '12 sq m'
                };
                
                setModalInitialStep(3);
                setModalInitialConfirmedBooking(finalBooking);
                setSelectedRoom(roomObj);
                
                const newUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
                return;
              }

              const finalBooking = {
                ...pendingBooking,
                status: 'confirmed',
                paymentId: `PAY-LIQPAY-GATEWAY-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
              };

              const savedBookings = safeLocalStorage.getItem('cozzydine_bookings_v21');
              const bookingsList = savedBookings ? JSON.parse(savedBookings) : [];
              
              const exists = bookingsList.some((b: any) => b.id === bookingId);
              if (!exists) {
                const updatedList = [finalBooking, ...bookingsList];
                safeLocalStorage.setItem('cozzydine_bookings_v21', JSON.stringify(updatedList));
              }

              safeLocalStorage.removeItem('cozzydine_pending_liqpay_booking');

              const roomsRaw = safeLocalStorage.getItem('cozzydine_rooms_v21');
              const roomsList = roomsRaw ? JSON.parse(roomsRaw) : [];
              const roomObj = roomsList.find((r: any) => r.id === finalBooking.roomId) || {
                id: finalBooking.roomId,
                name: finalBooking.roomName,
                price: finalBooking.totalPrice / 3,
                type: 'boutique-pod',
                description: 'Kyiv Boutique Pod Space',
                features: ['Free Wifi', 'Safe shelter access'],
                images: [],
                beds: 1,
                maxGuests: 2,
                bathrooms: 'Shared',
                space: '12 sq m'
              };

              setModalInitialStep(3);
              setModalInitialConfirmedBooking(finalBooking);
              setSelectedRoom(roomObj);

              if (typeof (window as any).gtag === 'function') {
                (window as any).gtag('event', 'purchase', {
                  transaction_id: finalBooking.id || 'BOOKING_' + Date.now(),
                  value: finalBooking.totalPrice || 0.00,
                  currency: 'UAH',
                  items: [{
                    item_name: finalBooking.roomName || 'Hostel Cozzydine Stay',
                    price: finalBooking.totalPrice || 0.00,
                    quantity: 1
                  }]
                });
              }

              const newUrl = window.location.origin + window.location.pathname;
              window.history.replaceState({}, document.title, newUrl);
            }
          }
        }
      } catch (err) {
        console.error('[App] Failed parsing payment_success parameters:', err);
      }
    }
  }, []);

  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [barCheckIn, setBarCheckIn] = useState(tomorrowStr);
  const [barCheckOut, setBarCheckOut] = useState(dayAfterStr);
  const [barGuests, setBarGuests] = useState(1);
  const [barRoomId, setBarRoomId] = useState('double-room');
  const [bookingForm, setBookingForm] = useState({
    guestName: '',
    guestEmail: '',
    checkIn: '',
    checkOut: '',
    guests: 1
  });
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlLang = urlParams.get('lang');
        if (urlLang && ['en', 'uk', 'de', 'es', 'fr'].includes(urlLang)) {
          safeLocalStorage.setItem('cozzydine_lang', urlLang);
          return urlLang;
        }
        return safeLocalStorage.getItem('cozzydine_lang') || 'en';
      } catch (e) {
        return 'en';
      }
    }
    return 'en';
  });

  useEffect(() => {
    const titles: Record<string, Record<string, string>> = {
      en: {
        home: "Hostel Cozzydine | Boutique Hostel in Kyiv City Center Near Metro",
        rooms: "Double Room, Private Room in Kyiv | Hostels Near Metro Station in Kyiv",
        about: "About Hostel Cozzydine | Top Hostel, Hostels in Kyiv City Center",
        contact: "Contact Hostel Cozzydine | Hostel Near Lybidska Metro Station in Kyiv"
      },
      uk: {
        home: "Хостел Cozzydine | Затишний хостел у Києві, хостели у Києві в центрі",
        rooms: "Двомісний номер, приватна кімната у Києві | Хостели біля станції метро",
        about: "Про хостел Cozzydine | Кращий хостел у центрі Києва біля метро",
        contact: "Контакти хостелу Cozzydine | Хостел біля метро Либідська у Києві"
      },
      es: {
        home: "Hostel Cozzydine | Hostal en Kiev y Hostales en el Centro de Kiev",
        rooms: "Habitación Doble y Habitación Privada en Kiev | Hostales Cerca del Metro",
        about: "Sobre Hostel Cozzydine | Hostal en el Centro de la Ciudad en Kiev",
        contact: "Contacto Hostel Cozzydine | Hostal Cerca de la Estación de Metro Lybidska"
      },
      fr: {
        home: "Hostel Cozzydine | Auberge à Kyiv et Auberges au Centre-Ville de Kyiv",
        rooms: "Chambre Double et Chambre Privée à Kyiv | Auberges Près du Métro",
        about: "À propos de l'Hostel Cozzydine | Auberge au Centre-Ville de Kyiv",
        contact: "Contact Hostel Cozzydine | Auberge Près de la Station de Métro Lybidska"
      }
    };

    const descriptions: Record<string, Record<string, string>> = {
      en: {
        home: "Hostel Cozzydine is the top boutique hostel in Kyiv among hostels in Kyiv. Book a private room in Kyiv or double room in Kyiv in the city center near Lybidska metro station with safe shelter and 24/7 care.",
        rooms: "Explore private double rooms and capsule bunk pods at Hostel Cozzydine Kyiv. Features orthopaedic mattresses, secure lockers, and climate control.",
        about: "Discover Hostel Cozzydine, a Ukrainian hospitality company established in 2020. Quality boutique lodging, guest reviews, and 24/7 care in central Kyiv.",
        contact: "Contact Hostel Cozzydine Kyiv 24/7. Step-by-step walking directions from Lybidska Metro Exit 2, Central Railway, and Central Bus Station."
      },
      uk: {
        home: "Cozzydine — безпечний бутик-хостел та коворкінг у центрі Києва. 2 хвилини від метро Либідська, цілодобова рецепція та безкоштовний Wi-Fi.",
        rooms: "Забронюйте двомісні номери та спальні капсули в хостелі Cozzydine Київ. Ортопедичні матраци, шафки для зберігання та кондиціонер.",
        about: "Дізнайтеся про хостел Cozzydine — українську гостинність з 2020 року. Затишні номери, відгуки гостей та цілодобова турбота в центрі Києва.",
        contact: "Зв'яжіться з хостелом Cozzydine Київ 24/7. Детальні маршрути від метро Либідська (Вихід №2), залізничного вокзалу та автостанції."
      },
      de: {
        home: "Hostel Cozzydine ist ein sicheres Boutique-Hostel & Nomad Space in Kiew. 2 Minuten von der U-Bahn Lybidska mit 24/7 Rezeption und WLAN.",
        rooms: "Buchen Sie Privatzimmer und Kapsel-Pods im Hostel Cozzydine Kiew. Mit orthopädischen Matratzen, Schließfächern und Klimaanlage.",
        about: "Erfahren Sie mehr über Hostel Cozzydine in Kiew seit 2020. Hochwertige Boutique-Unterkunft, Gästebewertungen und 24/7 Service im Zentrum.",
        contact: "Kontaktieren Sie das Hostel Cozzydine Kiew 24/7. Wegbeschreibungen von der U-Bahn Lybidska (Ausgang 2), dem Hauptbahnhof und Busbahnhof."
      },
      es: {
        home: "Hostel Cozzydine es un hostal boutique seguro en el centro de Kiev. A 2 minutos del metro Lybidska con recepción 24/7 y Wi-Fi gratis.",
        rooms: "Reserve habitaciones privadas y cápsulas pod en Hostel Cozzydine Kiev. Con colchones ortopédicos, taquillas seguras y aire acondicionado.",
        about: "Descubra Hostel Cozzydine en Kiev desde 2020. Alojamiento boutique de calidad, reseñas de huéspedes y atención 24/7 en el centro.",
        contact: "Contacte con Hostel Cozzydine Kiev 24/7. Direcciones desde el metro Lybidska (Salida 2), la estación central de trenes y autobuses."
      },
      fr: {
        home: "Hostel Cozzydine est une auberge boutique sécurisée au centre de Kyiv. À 2 minutes du métro Lybidska avec réception 24/7 et Wi-Fi gratuit.",
        rooms: "Réservez des chambres privées et des capsules pod à l'Hostel Cozzydine Kyiv. Matelas orthopédiques, casiers sécurisés et climatisation.",
        about: "Découvrez l'Hostel Cozzydine à Kiev depuis 2020. Hébergement boutique de quality, avis clients et service 24/7 au centre-ville.",
        contact: "Contactez l'Hostel Cozzydine Kyiv 24/7. Directives de marche depuis le métro Lybidska (Sortie 2), la gare centrale et la gare routière."
      }
    };

    const keywords: Record<string, Record<string, string>> = {
      en: {
        home: "Hostel in Kyiv. Hostels in Kyiv. Hostel near to metro station in Kyiv. Hostels near to metro station in Kyiv. Hostel near Lybidska metro station. Hostels near Lybidska metro station. Hostel in the city center in Kyiv. Hostels in the city center in Kyiv. Double room in Kyiv. Double in the city center in Kyiv. Private room in Kyiv. Private room in the city center in Kyiv. Double room near Lybidska metro station in Kyiv. Private room near Lybidska metro station in Kyiv. Double room near metro station in Kyiv. Private room near metro station in Kyiv. Хостел у Києві. Хостели у Києві.",
        rooms: "Cozzydine rooms, capsule hotel Kyiv, pod hostel Kyiv, Kyiv dormitory bunk beds, double private room Kyiv, cheap accommodation Kyiv, female hostel dorm, male hostel dorm, best beds Kyiv",
        about: "about Cozzydine, Chinju Ravi hospitality, Kyiv safe hotel, reinforced shelter Kyiv, hostel story, central Kyiv nomad workspace, Ukrainian heritage, reliable hostel Kyiv",
        contact: "Cozzydine contacts, Kyiv hostel address, directions to Lybidska Metro, Kyiv Central Station to hostel, Kyiv central bus station, local area guide, transit instructions Kyiv"
      },
      uk: {
        home: "Хостел Cozzydine, хостел Київ, бутик-хостел Київ, безпечний хостел Київ, хостел з укриттям, коворкінг Київ, житло біля метро Либідська, туристичний хостел, центр Києва",
        rooms: "кімнати Cozzydine, капсульний готель Київ, спальні капсули Київ, загальні номери Київ, двомісний номер Київ, дешевше житло Київ, жіночий хостел Київ, чоловічий хостел",
        about: "про Cozzydine, засновник хостелу, безпечне проживання Київ, хостел з бомбосховищем, історія хостелу, робочий простір хостел, туристичний гід Київ, незламна гостинність",
        contact: "контакти Cozzydine, адреса хостелу, маршрут від Либідської, залізничний вокзал хостел, автовокзал до хостелу, телефон хостелу, електронна пошта Cozzydine"
      },
      de: {
        home: "Hostel Cozzydine, Hostel Kiew, Boutique-Hostel Kiew, sicheres Hostel Kiew, Kiew Unterkunft mit Schutzraum, Co-Working Kiew, Lybidska U-Bahn",
        rooms: "Kapselhotel Kiew, Pod-Hostel Kiew, Kiew Schlafsaal, Doppelzimmer privat Kiew, günstige Unterkunft Kiew, Frauen-Schlafsaal Kiew",
        about: "Über Cozzydine, Chinju Ravi Gastfreundschaft, Kiew sicheres Hotel, Luftschutzbunker Kiew, Hostel-Geschichte, Nomaden-Arbeitsbereich Kiew",
        contact: "Cozzydine Contact, Kiew Hostel Adresse, Wegbeschreibung Lybidska, Kiew Hauptbahnhof zu Hostel, zentraler Busbahnhof"
      },
      es: {
        home: "Hostel Cozzydine, hostal Kiev, hostal boutique Kiev, hostal seguro Kiev, alojamiento con refugio Kiev, co-working Kiev, metro Lybidska",
        rooms: "hotel cápsula Kiev, hostal de pods Kiev, dormitorio compartido Kiev, habitación privada Kiev, alojamiento barato Kiev",
        about: "sobre Cozzydine, hospitalidad de Chinju Ravi, hotel seguro Kiev, refugio de bombardeo Kiev, espacio de trabajo digital Kiev",
        contact: "contactos Cozzydine, dirección de hostal Kiev, llegar desde metro Lybidska, de estación de tren a hostal"
      },
      fr: {
        home: "Hostel Cozzydine, auberge de jeunesse Kiev, auberge boutique Kiev, auberge sûre Kiev, hébergement avec abri Kiev, co-working Kiev",
        rooms: "hôtel capsule Kiev, auberge pod Kiev, dortoir partagé Kiev, chambre privée Kiev, hébergement pas cher Kiev",
        about: "à propos de Cozzydine, hospitalité Chinju Ravi, hôtel sécurisé Kiev, abri anti-aérien Kiev, espace nomade Kiev",
        contact: "contacts Cozzydine, adresse auberge Kiev, directions métro Lybidska, gare centrale vers auberge"
      }
    };

    const currentLang = currentLanguage || 'en';
    const activeTitle = titles[currentLang]?.[activePage] || titles.en.home;
    const activeDesc = descriptions[currentLang]?.[activePage] || descriptions.en.home;
    const activeKeywords = keywords[currentLang]?.[activePage] || keywords.en.home;

    document.title = activeTitle;

    try {
      document.documentElement.lang = currentLang;
    } catch (e) {
      console.warn("Could not update document lang:", e);
    }

    const updateOrCreateMeta = (selector: string, attrName: string, attrValue: string, content: string) => {
      try {
        let meta = document.querySelector(selector);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attrName, attrValue);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      } catch (e) {
        console.warn("Could not update meta tag:", selector, e);
      }
    };

    updateOrCreateMeta('meta[name="description"]', 'name', 'description', activeDesc);
    updateOrCreateMeta('meta[name="keywords"]', 'name', 'keywords', activeKeywords);

    const pagePath = getPathFromPage(activePage);
    const langSuffix = currentLang !== 'en' ? `?lang=${currentLang}` : '';
    const canonicalUrl = `https://cozzydine.com${pagePath}${langSuffix}`;

    updateOrCreateMeta('meta[property="og:title"]', 'property', 'og:title', activeTitle);
    updateOrCreateMeta('meta[property="og:description"]', 'property', 'og:description', activeDesc);
    updateOrCreateMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    updateOrCreateMeta('meta[property="og:image"]', 'property', 'og:image', 'https://cozzydine.com/cozzydine_logo.png');
    updateOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', activeTitle);
    updateOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', activeDesc);
    updateOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', 'https://cozzydine.com/cozzydine_logo.png');

    try {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    } catch (e) {
      console.warn("Could not update canonical link:", e);
    }
  }, [activePage, currentLanguage]);

  const getLogoUrl = () => resolveMediaUrl(cozzydineLogoMain, 'logo');
  const getReceptionUrl = () => resolveMediaUrl(cozzydineReceptionMain, 'about-reception');

  const handleLanguageChange = (lang: string) => {
    setCurrentLanguage(lang);
    try {
      safeLocalStorage.setItem('cozzydine_lang', lang);
    } catch (e) {
      console.warn('Could not write language to safeLocalStorage:', e);
    }
  };

  const t = (key: string) => {
    return translations[currentLanguage]?.[key] || translations['en']?.[key] || key;
  };

  const handleProceedBookingBar = (e: React.FormEvent) => {
    e.preventDefault();
    const room = fallbackRooms.find(r => r.id === barRoomId) || fallbackRooms[0];
    
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'begin_checkout', {
        currency: 'UAH',
        items: [{
          item_name: room.name || 'Hostel Cozzydine Accommodation',
          price: room.pricePerNight || 0
        }]
      });
    }

    setModalInitialStep(null);
    setModalInitialConfirmedBooking(null);
    setSelectedRoom(room);
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactName || !contactEmail || !contactMessage) return;
    
    const subject = `Inquiry from ${contactName}`;
    const body = `${contactMessage}\n\nFrom: ${contactName} <${contactEmail}>`;
    const mailtoUrl = `mailto:cozzydinekyiv@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.location.href = mailtoUrl;

    setContactSubmitted(true);
    setTimeout(() => {
      setContactSubmitted(false);
      setContactName('');
      setContactEmail('');
      setContactMessage('');
    }, 4000);
  };

  const handleContactFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'contactName') setContactName(value);
    if (name === 'contactEmail') setContactEmail(value);
    if (name === 'contactMessage') setContactMessage(value);
  };

  const transitContent: Record<string, { address: string, tel: string, lybidskaSteps: string[], palatsSteps: string[], trainSteps: string[], busSteps: string[], centralBusSteps: string[] }> = {
    en: {
      address: `Address: ${physicalAddress}`,
      tel: `Call: ${phoneNumber}`,
      lybidskaSteps: [
        "Take Metro Exit No.2: The first passage right next to the main exit of the metro station, which leads to Exit No.2 and brings you directly up onto Antonovycha Street.",
        "No Street Crossing Required: The hostel is on the exact same side of the street as Exit No. 2. Walk ahead on Antonovycha Street (opposite Ocean Plaza shopping mall).",
        "Walk Ahead for 2 Minutes: Continue straight along this exact same sidewalk for about 140 meters (1.5 to 2 minute walk) past the Synevo medical laboratory.",
        "Pass Through the Metal Gate: Upon reaching building #169 (which is the next building immediately after Synevo), turn right through the metal gate, then immediately turn left into the inner courtyard.",
        "Find Hostel Cozzydine: You've arrived! The hostel is inside the courtyard at the corner painted in black & white, on the ground floor and semi-basement."
      ],
      palatsSteps: [
        "Board the Metro: Enter Maidan Nezalezhnosti Subway Station and take the Blue Line heading south toward Teremky.",
        "Ride South for 5 Stops: Pass through Ploshcha Ukrainskykh Heroiv, Olimpiiska, Palats Ukrayina, and alight at Lybidska Station (approx. 10 minutes total transit ride).",
        "Take Metro Exit No.2: The first passage right next to the main exit of the metro station, which leads to Exit No.2 and brings you directly up onto Antonovycha Street.",
        "No Street Crossing Required: The hostel is on the exact same side of the street as Exit No. 2. Walk ahead on Antonovycha Street (opposite Ocean Plaza shopping mall).",
        "Walk Ahead for 2 Minutes: Continue straight along this exact same sidewalk for about 140 meters (1.5 to 2 minute walk) past the Synevo medical laboratory.",
        "Pass Through the Metal Gate: Upon reaching building #169 (which is the next building immediately after Synevo), turn right through the metal gate, then immediately turn left into the inner courtyard.",
        "Find Hostel Cozzydine: You've arrived! The hostel is inside the courtyard at the corner painted in black & white, on the ground floor and semi-basement."
      ],
      trainSteps: [
        "Board the Metro at Vokzalna Station (Red Line) toward Forest (Lisova).",
        "Transfer at Khreshchatyk / Maidan Nezalezhnosti to the Blue Line heading south.",
        "Ride south and alight at Lybidska Station (approx. 12 minutes total transit).",
        "Take Metro Exit No.2: The first passage right next to the main exit of the metro station, which leads to Exit No.2 and brings you directly up onto Antonovycha Street.",
        "No Street Crossing Required: The hostel is on the exact same side of the street as Exit No. 2. Walk ahead on Antonovycha Street (opposite Ocean Plaza shopping mall).",
        "Walk Ahead for 2 Minutes: Continue straight along this exact same sidewalk for about 140 meters (1.5 to 2 minute walk) past the Synevo medical laboratory.",
        "Pass Through the Metal Gate: Upon reaching building #169 (which is the next building immediately after Synevo), turn right through the metal gate, then immediately turn left into the inner courtyard.",
        "Find Hostel Cozzydine: You've arrived! The hostel is inside the courtyard at the corner painted in black & white, on the ground floor and semi-basement."
      ],
      busSteps: [
        "Get off at the Lybidska Bus/Trolleybus Stop (located right near the metro station) and head into the pedestrian underpass. Take the first passage right next to the main exit of the metro station, which leads to Exit No.2 and brings you directly up onto Antonovycha Street.",
        "No Street Crossing Required: The hostel is on the exact same side of the street as Exit No. 2. Walk ahead on Antonovycha Street (opposite Ocean Plaza shopping mall).",
        "Walk Ahead for 2 Minutes: Continue straight along this exact same sidewalk for about 140 meters (1.5 to 2 minute walk) past the Synevo medical laboratory.",
        "Pass Through the Metal Gate: Upon reaching building #169 (which is the next building immediately after Synevo), turn right through the metal gate, then immediately turn left into the inner courtyard.",
        "Find Hostel Cozzydine: You've arrived! The hostel is inside the courtyard at the corner painted in black & white, on the ground floor and semi-basement."
      ],
      centralBusSteps: [
        "Walk to Metro: Walk to Demiivska Metro Station, just a 2-minute walk (200m) from the main Central Bus terminal.",
        "Ride to Lybidska: Take the Blue Line metro northbound towards Heroiv Dnipra for exactly 1 stop to Lybidska Metro Station.",
        "Take Metro Exit No.2: The first passage right next to the main exit of the metro station, which leads to Exit No.2 and brings you directly up onto Antonovycha Street.",
        "No Street Crossing Required: The hostel is on the exact same side of the street as Exit No. 2. Walk ahead on Antonovycha Street (opposite Ocean Plaza shopping mall).",
        "Walk Ahead for 2 Minutes: Continue straight along this exact same sidewalk for about 140 meters (1.5 to 2 minute walk) past the Synevo medical laboratory.",
        "Pass Through the Metal Gate: Upon reaching building #169 (which is the next building immediately after Synevo), turn right through the metal gate, then immediately turn left into the inner courtyard.",
        "Find Hostel Cozzydine: You've arrived! The hostel is inside the courtyard at the corner painted in black & white, on the ground floor and semi-basement."
      ]
    },
    uk: {
      address: `Адреса: ${physicalAddress}`,
      tel: `Тел: ${phoneNumber}`,
      lybidskaSteps: [
        "Вихід з метро: Вийдіть зі станції метро «Либідська» через Вихід №2 на вулицю Антоновича.",
        "Не потрібно переходити вулицю: Хостел знаходиться на тому ж боці вулиці, що й Вихід №2. Просто йдіть вперед по вулиці Антоновича (навпроти торгового центру Ocean Plaza).",
        "Йдіть вперед 2 хвилини: Продовжуйте йти прямо по цьому ж тротуару приблизно 140 метрів (від 1,5 до 2 хвилин ходьби) повз медичну лабораторію Сінево.",
        "Поверніть через металеві ворота: Біля будинку №169 (одразу за «Сінево») поверніть праворуч крізь металеві ворота, після чого одразу поверніть ліворуч та зайдіть у внутрішній двір.",
        "Вхід до Cozzydine: Ви прибули! Хостел знаходиться всередині двору в кутку, розфарбованому в чорно-білі кольори, на першому та цокольному поверхах."
      ],
      palatsSteps: [
        "Сядьте на метро: Зайдіть на станцію метро «Майдан Незалежності» та сідайте на Синю лінію у напрямку Теремків.",
        "Проїдьте 5 зупинок на південь: Проїдьте через станції Площа Українських Героїв, Олімпійська, Палац Україна та вийдіть на станції «Либідська» (загальний час у дорозі близько 10 хв).",
        "Вихід з метро: Вийдіть зі станції метро «Либідська» через Вихід №2 на вулицю Антоновича.",
        "Не потрібно переходити вулицю: Хостел знаходиться на тому ж боці вулиці, що й Вихід №2. Просто йдіть вперед по вулиці Антоновича (навпроти торгового центру Ocean Plaza).",
        "Йдіть вперед 2 хвилини: Продовжуйте йти прямо по цьому ж тротуару приблизно 140 метрів (від 1,5 до 2 хвилин ходьби) повз медичну лабораторію Сінево.",
        "Поверніть через металеві ворота: Біля будинку №169 (одразу за «Сінево») поверніть праворуч крізь металеві ворота, після чого одразу поверніть ліворуч та зайдіть у внутрішній двір.",
        "Вхід до Cozzydine: Ви прибули! Хостел знаходиться всередині двору в кутку, розфарбованому в чорно-білі кольори, на першому та цокольному поверхах."
      ],
      trainSteps: [
        "Сядьте на метро на станції Вокзальна (Червона лінія) у напрямку Лісової.",
        "Зробіть пересадку на станції Хрещатик / Майдан Незалежності на Синю лінію у напрямку Теремків.",
        "Їїдьте до станції Либідська (загальний час у дорозі близько 12 хв).",
        "Вихід з метро: Вийдіть зі станції метро «Либідська» через Вихід №2 на вулицю Антоновича.",
        "Не потрібно переходити вулицю: Хостел знаходиться на тому ж боці вулиці, що й Вихід №2. Просто йдіть вперед по вулиці Антоновича (навпроти торгового центру Ocean Plaza).",
        "Йдіть вперед 2 хвилини: Продовжуйте йти прямо по цьому ж тротуару приблизно 140 метрів (від 1,5 до 2 хвилин ходьби) повз медичну лабораторію Сінево.",
        "Поверніть через металеві ворота: Біля будинку №169 (одразу за «Сінево») поверніть праворуч крізь металеві ворота, після чого одразу поверніть ліворуч та зайдіть у внутрішній двір.",
        "Вхід до Cozzydine: Ви прибули! Хостел знаходиться всередині двору в кутку, розфарбованому в чорно-білі кольори, на першому та цокольному поверхах."
      ],
      busSteps: [
        "Зупинка «Либідська»: Вийдіть на автобусній/тролейбусній зупинці (розташованій поруч зі станцією метро). Скористайтеся підземними пішохідними переходами та вийдіть через Вихід №2.",
        "Не потрібно переходити вулицю: Хостел знаходиться на тому ж боці вулиці, що й Вихід №2. Просто йдіть вперед по вулиці Антоновича (навпроти торгового центру Ocean Plaza).",
        "Йдіть вперед 2 хвилини: Продовжуйте йти прямо по цьому ж тротуару приблизно 140 метрів (від 1,5 до 2 хвилин ходьби) повз медичну лабораторію Сінево.",
        "Поверніть через металеві ворота: Біля будинку №169 (одразу за «Сінево») поверніть праворуч крізь металеві ворота, після чого одразу поверніть ліворуч та зайдіть у внутрішній двір.",
        "Вхід до Cozzydine: Ви прибули! Хостел знаходиться всередині двору в кутку, розфарбованому в чорно-білі кольори, на першому та цокольному поверхах."
      ],
      centralBusSteps: [
        "Підійдіть до метро: Підійдіть до станції метро «Деміївська», яка знаходиться за 2 хвилини ходьби (200 м) від головного терміналу Автовокзалу.",
        "Проїдьте до Либідської: Сідайте на Синю лінію метро в напрямку «Героїв Дніпра» та проїдьте рівно 1 зупинку до станції «Либідська».",
        "Вихід з метро: Вийдіть зі станції метро «Либідська» через Вихід №2 на вулицю Антоновича.",
        "Не потрібно переходити вулицю: Хостел знаходиться на тому ж боці вулиці, що й Вихід №2. Просто йдіть вперед по вулиці Антоновича (навпроти торгового центру Ocean Plaza).",
        "Йдіть вперед 2 хвилини: Продовжуйте йти прямо по цьому ж тротуару приблизно 140 метрів (від 1,5 до 2 хвилин ходьби) повз медичну лабораторію Сінево.",
        "Поверніть через металеві ворота: Біля будинку №169 (одразу за «Сінево») поверніть праворуч крізь металеві ворота, після чого одразу поверніть ліворуч та зайдіть у внутрішній двір.",
        "Вхід до Cozzydine: Ви прибули! Хостел знаходиться всередині двору в кутку, розфарбованому в чорно-білі кольори, на першому та цокольному поверхах."
      ]
    },
    de: {
      address: `Adresse: ${physicalAddress}`,
      tel: `Anrufen: ${phoneNumber}`,
      lybidskaSteps: [
        "Nehmen Sie den Ausgang Nr. 2 an der U-Bahn-Station Lybidska (Blaue Linie) zur Antonovycha-Straße.",
        "Keine Straßenüberquerung erforderlich: Das Hostel befindet sich auf der gleichen Straßenseite wie der Ausgang Nr. 2. Gehen Sie einfach auf der Antonovycha-Straße vorwärts (gegenüber dem Einkaufszentrum Ocean Plaza).",
        "Gehen Sie 2 Minuten geradeaus: Gehen Sie auf genau diesem Gehweg ca. 140 m (1,5 bis 2 Minuten Gehzeit) geradeaus weiter, vorbei am medizinischen Labor Synevo.",
        "Wenn Sie das Gebäude 169 erreichen, biegen Sie rechts durch das Metalltor ab, biegen Sie sofort links ab und gehen Sie in den Innenhof des Gebäudes.",
        "Das Hostel Cozzydine befindet sich im Innenhof in der schwarz-weiß gestrichenen Ecke, im Erdgeschoss und im Untergeschoss."
      ],
      palatsSteps: [
        "In die U-Bahn einsteigen: Betreten Sie die U-Bahn-Station Maidan Nezalezhnosti (Blaue Linie) und fahren Sie in Richtung Süden (Teremky).",
        "Fahren Sie 5 Stationen nach Süden: Fahren Sie vorbei an Ploshcha Ukrainskykh Heroiv, Olimpiiska, Palats Ukrayina und steigen Sie an der Station Lybidska aus (ca. 10 Minuten Fahrtzeit).",
        "Nehmen Sie den Ausgang Nr. 2 an der U-Bahn-Station Lybidska (Blaue Linie) zur Antonovycha-Straße.",
        "Keine Straßenüberquerung erforderlich: Das Hostel befindet sich auf der gleichen Straßenseite wie der Ausgang Nr. 2. Gehen Sie einfach auf der Antonovycha-Straße vorwärts (gegenüber dem Einkaufszentrum Ocean Plaza).",
        "Gehen Sie 2 Minuten geradeaus: Gehen Sie auf genau diesem Gehweg ca. 140 m (1,5 bis 2 Minuten Gehzeit) geradeaus weiter, vorbei am medizinischen Labor Synevo.",
        "Wenn Sie das Gebäude 169 erreichen, biegen Sie rechts durch das Metalltor ab, biegen Sie sofort links ab und gehen Sie in den Innenhof des Gebäudes.",
        "Das Hostel Cozzydine befindet sich im Innenhof in der schwarz-weiß gestrichenen Ecke, im Erdgeschoss und im Untergeschoss."
      ],
      trainSteps: [
        "Steigen Sie an der Station Vokzalna (Rote Linie) in die U-Bahn in Richtung Forest (Lisova) ein.",
        "Steigen Sie an der Station Khreshchatyk / Maidan Nezalezhnosti in die Blaue Linie um, die nach Süden fährt.",
        "Fahren Sie nach Süden und steigen Sie am Bahnhof Lybidska aus (ca. 12 Minuten Fahrtzeit).",
        "Nehmen Sie den Ausgang Nr. 2 an der U-Bahn-Station Lybidska (Blaue Linie) zur Antonovycha-Straße.",
        "Keine Straßenüberquerung erforderlich: Das Hostel befindet sich auf der gleichen Straßenseite wie der Ausgang Nr. 2. Gehen Sie einfach auf der Antonovycha-Straße vorwärts (gegenüber dem Einkaufszentrum Ocean Plaza).",
        "Gehen Sie 2 Minuten geradeaus: Gehen Sie auf genau diesem Gehweg ca. 140 m (1,5 bis 2 Minuten Gehzeit) geradeaus weiter, vorbei am medizinischen Labor Synevo.",
        "Wenn Sie das Gebäude 169 erreichen, biegen Sie rechts durch das Metalltor ab, biegen Sie sofort links ab und gehen Sie in den Innenhof des Gebäudes.",
        "Das Hostel Cozzydine befindet sich im Innenhof in der schwarz-weiß gestrichenen Ecke, im Erdgeschoss und im Untergeschoss."
      ],
      busSteps: [
        "Steigen Sie an der Bus-/Trolleybushaltestelle Lybidska aus (in der Nähe der U-Bahn-Station). Nutzen Sie die Fußgängerunterführungen und nehmen Sie den Ausgang Nr. 2.",
        "Keine Straßenüberquerung erforderlich: Das Hostel befindet sich auf der gleichen Straßenseite wie der Ausgang Nr. 2. Gehen Sie einfach auf der Antonovycha-Straße vorwärts (gegenüber dem Einkaufszentrum Ocean Plaza).",
        "Gehen Sie 2 Minuten geradeaus: Gehen Sie auf genau diesem Gehweg ca. 140 m (1,5 bis 2 Minuten Gehzeit) geradeaus weiter, vorbei am medizinischen Labor Synevo.",
        "Wenn Sie das Gebäude 169 erreichen, biegen Sie rechts durch das Metalltor ab, biegen Sie sofort links ab und gehen Sie in den Innenhof des Gebäudes.",
        "Das Hostel Cozzydine befindet sich im Innenhof in der schwarz-weiß gestrichenen Ecke, im Erdgeschoss und im Untergeschoss."
      ],
      centralBusSteps: [
        "Gehen Sie zur U-Bahn: Gehen Sie zur U-Bahn-Station Demiivska, die nur 2 Gehminuten (200 m) vom Hauptterminal des Zentralen Busbahnhofs entfernt ist.",
        "Fahren Sie nach Lybidska: Nehmen Sie die U-Bahn der Blauen Linie in Richtung Norden (Heroiv Dnipra) und fahren Sie genau 1 Station bis zur Station Lybidska.",
        "Nehmen Sie den Ausgang Nr. 2 an der U-Bahn-Station Lybidska (Blaue Linie) zur Antonovycha-Straße.",
        "Keine Straßenüberquerung erforderlich: Das Hostel befindet sich auf der gleichen Straßenseite wie der Ausgang Nr. 2. Gehen Sie einfach auf der Antonovycha-Straße vorwärts (gegenüber dem Einkaufszentrum Ocean Plaza).",
        "Gehen Sie 2 Minuten geradeaus: Gehen Sie auf genau diesem Gehweg ca. 140 m (1,5 bis 2 Minuten Gehzeit) geradeaus weiter, vorbei am medizinischen Labor Synevo.",
        "Wenn Sie das Gebäude 169 erreichen, biegen Sie rechts durch das Metalltor ab, biegen Sie sofort links ab und gehen Sie in den Innenhof des Gebäudes.",
        "Das Hostel Cozzydine befindet sich im Innenhof in der schwarz-weiß gestrichenen Ecke, im Erdgeschoss und im Untergeschoss."
      ]
    },
    es: {
      address: `Dirección: ${physicalAddress}`,
      tel: `Llamar: ${phoneNumber}`,
      lybidskaSteps: [
        "¡Tome la Salida No. 2 en la estación de metro Lybidska (Línea Azul) hacia la calle Antonovycha!",
        "No es necesario cruzar la calle: El hostal se encuentra exactamente en la misma acera de la salida No. 2. Simplemente camine adelante por la calle Antonovycha (frente al centro comercial Ocean Plaza).",
        "Camine adelante durante 2 minutes: Continúe recto por esta misma acera durante unos 140 metros (entre 1.5 y 2 minutos a pie) pasando por delante del laboratorio médico Synevo.",
        "Al llegar al edificio 169, gire a la derecha por la puerta de metal, gire inmediatamente a la izquierda e ingrese al patio del edificio.",
        "El Hostal Cozzydine está ubicado dentro del patio, en la esquina pintada de blanco y negro, en la planta baja y el sótano."
      ],
      palatsSteps: [
        "Suba al metro: Ingrese a la estación de metro Maidan Nezalezhnosti (Línea Azul) y tome la dirección sur hacia Teremky.",
        "Viaje 5 estaciones hacia el sur: Pase por Ploshcha Ukrainskykh Heroiv, Olimpiiska, Palats Ukrayina y bájese en la estación Lybidska (aproximadamente 10 minutos de trayecto).",
        "¡Tome la Salida No. 2 en la estación de metro Lybidska (Línea Azul) hacia la calle Antonovycha!",
        "No es necesario cruzar la calle: El hostal se encuentra exactamente en la misma acera de la salida No. 2. Simplemente camine adelante por la calle Antonovycha (frente al centro comercial Ocean Plaza).",
        "Camine adelante durante 2 minutes: Continúe recto por esta misma acera durante unos 140 metros (entre 1.5 y 2 minutos a pie) pasando por delante del laboratorio médico Synevo.",
        "Al llegar al edificio 169, gire a la derecha por la puerta de metal, gire inmediatamente a la izquierda e ingrese al patio del edificio.",
        "El Hostal Cozzydine está ubicado dentro del patio, en la esquina pintada de blanco y negro, en la planta baja y el sótano."
      ],
      trainSteps: [
        "Aborde el metro en la estación Vokzalna (Línea Roja) hacia Forest (Lisova).",
        "Transborde en Khreshchatyk / Maidan Nezalezhnosti a la Línea Azul con dirección al sur.",
        "Viaje hacia el sur y bájese en la estación Lybidska (aproximadamente 12 minutos de trayecto total).",
        "¡Tome la Salida No. 2 en la estación de metro Lybidska (Línea Azul) hacia la calle Antonovycha!",
        "No es necesario cruzar la calle: El hostal se encuentra exactamente en la misma acera of the salida No. 2. Simplemente camine adelante por la calle Antonovycha (frente al centro comercial Ocean Plaza).",
        "Camine adelante durante 2 minutes: Continúe recto por esta misma acera durante unos 140 metros (entre 1.5 y 2 minutos a pie) pasando por delante del laboratorio médico Synevo.",
        "Al llegar al edificio 169, gire a la derecha por la puerta de metal, gire inmediatamente a la izquierda e ingrese al patio del edificio.",
        "El Hostal Cozzydine está ubicado dentro del patio, en la esquina pintada de blanco y negro, en la planta baja y el sótano."
      ],
      busSteps: [
        "Bájese en la parada de autobús/trolebús Lybidska (ubicada justo al lado de la estación de metro). ¡Utilice los pasos peatonales subterráneos y tome la Salida No. 2!",
        "No es necesario cruzar la calle: El hostal se encuentra exactamente en la misma acera of the salida No. 2. Simplemente camine adelante por la calle Antonovycha (frente al centro comercial Ocean Plaza).",
        "Camine adelante durante 2 minutes: Continúe recto por esta misma acera durante unos 140 metros (entre 1.5 y 2 minutos a pie) pasando por delante del laboratorio médico Synevo.",
        "Al llegar al edificio 169, gire a la derecha por la puerta de metal, gire inmediatamente a la izquierda e ingrese al patio del edificio.",
        "El Hostal Cozzydine está ubicado dentro del patio, en la esquina pintada de blanco y negro, en la planta baja y el sótano."
      ],
      centralBusSteps: [
        "Camine al metro: Camine hacia la estación de metro Demiivska, que está a solo 2 minutos a pie (200m) de la terminal principal del autobús central.",
        "Viaje a Lybidska: Tome el metro de la Línea Azul en dirección norte hacia Heroiv Dnipra durante exactamente 1 parada hasta la estación de metro Lybidska.",
        "¡Tome la Salida No. 2 en la estación de metro Lybidska (Línea Azul) hacia la calle Antonovycha!",
        "No es necesario cruzar la calle: El hostal se encuentra exactamente en la misma acera of the salida No. 2. Simplemente camine adelante por la calle Antonovycha (frente al centro comercial Ocean Plaza).",
        "Camine adelante durante 2 minutes: Continúe recto por esta misma acera durante unos 140 metros (entre 1.5 y 2 minutos a pie) pasando por delante del laboratorio médico Synevo.",
        "Al llegar al edificio 169, gire a la derecha por la puerta de metal, gire inmediatamente a la izquierda e ingrese al patio del edificio.",
        "El Hostal Cozzydine está ubicado dentro del patio, en la esquina pintada de blanco y negro, en la planta baja y el sótano."
      ]
    },
    fr: {
      address: `Adresse : ${physicalAddress}`,
      tel: `Tél : ${phoneNumber}`,
      lybidskaSteps: [
        "Prenez la Sortie n° 2 à la station de métro Lybidska (Ligne Bleue) pour rejoindre la rue Antonovycha.",
        "Pas besoin de traverser la rue : L'auberge est située du même côté de la rue que la sortie n° 2. Marchez simplement tout droit sur la rue Antonovycha (en face du centre commercial Ocean Plaza).",
        "Marchez tout droit pendant 2 minutes : Continuez tout droit sur ce même trottoir sur environ 140 mètres (1,5 à 2 minutes de marche) en passant devant le laboratoire médical Synevo.",
        "En arrivant au bâtiment 169, tournez à droite par la grille métallique, puis tournez immédiatement à gauche pour entrer dans la cour du bâtiment.",
        "L'auberge Cozzydine est située à l'intérieur de la cour, dans l'angle peint en noir et blanc, au rez-de-chaussée et au sous-sol."
      ],
      palatsSteps: [
        "Prenez le métro : Entrez dans la station de métro Maidan Nezalezhnosti (Ligne Bleue) et prenez la direction sud vers Teremky.",
        "Voyagez 5 stations vers le sud : Passez par Ploshcha Ukrainskykh Heroiv, Olimpiiska, Palats Ukrayina et descendez à la station Lybidska (environ 10 minutes de trajet).",
        "Prenez la Sortie n° 2 à la station de métro Lybidska (Ligne Bleue) pour rejoindre la rue Antonovycha.",
        "Pas besoin de traverser la rue : L'auberge est située du même côté de la rue que la sortie n° 2. Marchez simplement tout droit sur la rue Antonovycha (en face du centre commercial Ocean Plaza).",
        "Marchez tout droit pendant 2 minutes : Continuez tout droit sur ce même trottoir sur environ 140 mètres (1,5 à 2 minutes de marche) en passant devant le laboratoire médical Synevo.",
        "En arrivant au bâtiment 169, tournez à droite par la grille métallique, puis tournez immédiatement à gauche pour entrer dans la cour du bâtiment.",
        "L'auberge Cozzydine est située à l'intérieur de la cour, dans l'angle peint en noir et blanc, au rez-de-chaussée et au sous-sol."
      ],
      trainSteps: [
        "Prenez le métro à la station Vokzalna (Ligne Rouge) en direction de Forest (Lisova).",
        "Faites un changement à Khreshchatyk / Maidan Nezalezhnosti vers la Ligne Bleue direction sud.",
        "Allez vers le sud et descendez à la station Lybidska (environ 12 minutes de trajet au total).",
        "Prenez la Sortie n° 2 à la station de métro Lybidska (Ligne Bleue) pour rejoindre la rue Antonovycha.",
        "Pas besoin de traverser la rue : L'auberge est située du même côté de la rue que la sortie n° 2. Marchez simplement tout droit sur la rue Antonovycha (en face du centre commercial Ocean Plaza).",
        "Marchez tout droit pendant 2 minutes : Continuez tout droit sur ce même trottoir sur environ 140 mètres (1,5 à 2 minutes de marche) en passant devant le laboratoire médical Synevo.",
        "En arrivant au bâtiment 169, tournez à droite par la grille métallique, puis tournez immédiatement à gauche pour entrer dans la cour du bâtiment.",
        "L'auberge Cozzydine est située à l'intérieur de la cour, dans l'angle peint en noir et blanc, au rez-de-side et au sous-sol."
      ],
      busSteps: [
        "Descendez à l'arrêt d'autobus/trolleybus Lybidska (situé juste à côté de la station de métro). Utilisez les passages piétons souterrains et prenez la Sortie n° 2.",
        "Pas besoin de traverser la rue : L'auberge est située du même côté de la rue que la sortie n° 2. Marchez simplement tout droit sur la rue Antonovycha (en face du centre commercial Ocean Plaza).",
        "Marchez tout droit pendant 2 minutes : Continuez tout droit sur ce même trottoir on environ 140 mètres (1,5 à 2 minutes de marche) en passant devant le laboratoire médical Synevo.",
        "En arrivant au bâtiment 169, tournez à droite par la grille métallique, puis tournez immédiatement à gauche pour entrer dans la cour du bâtiment.",
        "L'auberge Cozzydine est située à l'intérieur de la cour, dans l'angle peint en noir et blanc, au rez-de-chaussée et au sous-sol."
      ],
      centralBusSteps: [
        "Marchez vers le métro : Marchez jusqu'à la station de métro Demiivska, à seulement 2 minutes à pied (200 m) du terminal de la gare routière centrale.",
        "Allez à Lybidska : Prenez le métro de la Ligne Bleue direction nord vers Heroiv Dnipra et descendez après exactement 1 arrêt à la station Lybidska.",
        "Prenez la Sortie n° 2 à la station de métro Lybidska (Ligne Bleue) pour rejoindre la rue Antonovycha.",
        "Pas besoin de traverser la rue : L'auberge est située du même côté de la rue que la sortie n° 2. Marchez simplement tout droit sur la rue Antonovycha (en face du centre commercial Ocean Plaza).",
        "Marchez tout droit pendant 2 minutes : Continuez tout droit sur ce même trottoir on environ 140 mètres (1,5 à 2 minutes de marche) en passant devant le laboratoire médical Synevo.",
        "En arrivant au bâtiment 169, tournez à droite par la grille métallique, puis tournez immédiatement à gauche pour entrer dans la cour du bâtiment.",
        "L'auberge Cozzydine est située à l'intérieur de la cour, dans l'angle peint en noir et blanc, au rez-de-chaussée et au sous-sol."
      ]
    }
  };

  const tc = transitContent[currentLanguage] || transitContent.en;

  const faqs: Record<string, { q: string, a: string }[]> = {
    en: [
      { q: "What are your check-in and check-out times?", a: "Check-in is from 14:00 (2:00 PM), and check-out is by 11:00 AM. Since our reception desk is open 24/7, you can arrive or depart at any hour of the night or early morning!" },
      { q: "What are the rules regarding quiet hours for the pods?", a: `To guarantee pristine rest for every single guest, strict quiet hours are enforced inside all dorm rooms from ${quietHours}.` },
      { q: "Do you have a secure shelter for safety and air raid alerts?", a: "Absolutely! The hostel is located on the ground floor and underground floor, and the nearby Lybidska Metro Station serves as an underground public shelter." }
    ],
    uk: [
      { q: "Який у вас час заїзду та виїзду?", a: "Час реєстрації починається о 14:00, а виїзд — до 11:00. Оскільки наша стійка реєстрації працює цілодобово (24/7), ви можете заїхати або виїхати в будь-який зручний час." },
      { q: "Які правила щодо тихих годин біля спальних капсул?", a: `Для забезпечення спокійного відпочинку гостей мы дотримуємося суворих тихих годин у спальних зонах з ${quietHours}.` },
      { q: "Чи є у вас безпечне укриття на випадок повітряних тривог?", a: "Так, безпека є важливим аспектом. Наш хостел розташований на цокольному (напівпідвальному) та підвальному поверсі, а поруч знаходиться станція метро 'Либідська', яка служить глибоким підземним укриттям." }
    ],
    de: [
      { q: "Wie sind Ihre Check-in- und Check-out-Zeiten?", a: "Der Check-in ist ab 14:00 Uhr und der Check-out bis 11:00 Uhr. Da unsere Rezeption rund um die Uhr (24/7) besetzt ist, können Sie zu jeder Tages- oder Nachtzeit an- oder abreisen!" },
      { q: "Wie lauten die Regeln für die Ruhezeiten in den Schlafsälen?", a: `Um allen Gästen eine ungestörte Nachtruhe zu garantieren, gelten in allen Schlafsälen strenge Ruhezeiten von ${quietHours}.` },
      { q: "Haben Sie einen sicheren Schutzraum für Luftschutzkeller?", a: "Ja! Das Hostel befindet sich im Erdgeschoss und im Untergeschoss, und die nahe gelegene U-Bahn-Station Lybidska dient als öffentlicher Schutzraum." }
    ],
    es: [
      { q: "¿Cuáles son los horarios de entrada y salida?", a: "La entrada es a partir de las 14:00 y la salida es antes de las 11:00 AM. Dado que nuestra recepción está abierta las 24 horas, los 7 días de la semana, ¡puede llegar o partir a cualquier hora de la noche o de la madrugada!" },
      { q: "¿Cuáles son las reglas sobre las horas de silencio?", a: `Para garantizar un descanso óptimo para todos los huéspedes, se aplican horas de silencio estrictas en todos los dormitorios de las zonas compartidas a partir de las ${quietHours}.` },
      { q: "Tienen un refugio seguro para alertas y alarmas aéreas?", a: "¡Sí! El hostal está ubicado en la planta baja y el sótano, y la cercana estación de metro Lybidska sirve como un refugio público subterráneo." }
    ],
    fr: [
      { q: "Quels sont vos horaires d'arrivée et de départ ?", a: "L'enregistrement s'effectue à partir de 14h00 et le départ doit se faire avant 11h00. Notre réception étant ouverte 24/7, vous pouvez arriver ou partir à n'importe quelle heure de la nuit ou du matin !" },
      { q: "Quelles sont les règles concernant les heures de silence pour les dortoirs ?", a: `Pour garantir un repos parfait à chaque voyageur, des heures de silence strictes sont appliquées dans tous les dortoirs de ${quietHours}.` },
      { q: "Disposez-vous d'un abri sûr contre les alertes aériennes ?", a: "Oui ! L'auberge est située au rez-de-chaussée et au sous-sol, und la station de métro Lybidska à proximité sert d'abri public souterrain." }
    ]
  };

  return (
    <>
      <div
        ref={containerRef}
        id="cozzydine-viewport-container"
        className={`${theme === 'light' ? 'light-mode' : ''}`}
        style={{
          width: '100%',
          overflowX: 'hidden',
          overflowY: 'hidden',
          height: scaledHeight === 'auto' ? 'auto' : `${scaledHeight}px`,
          position: 'relative',
          backgroundColor: 'var(--bg-app)',
        }}
      >
      <div 
        ref={contentRef}
        id="cozzydine-app-root" 
        className={`min-h-screen overflow-x-hidden ${theme === 'light' ? 'light-mode' : ''} bg-[var(--bg-app)] text-[#FAF9F6] font-sans antialiased selection:bg-emerald-900 selection:text-emerald-100`}
        style={{
    width: '1280px',
    minWidth: '1280px',
    maxWidth: '1280px',
    transform: viewportScale < 1 ? `scale(${viewportScale})` : undefined,
    transformOrigin: 'top left',
    position: 'relative',
    margin: viewportScale < 1 ? 0 : '0 auto',
  }}
      >
      
      {announcement && announcement.active && !isAnnouncementDismissed && (
        <div 
          id="cozzydine-alert-bar"
          className={`relative z-45 px-4 py-2 text-white border-b flex items-center justify-between shadow-lg select-none transition-all duration-250 ${
            announcement.type === 'promo' ? 'bg-gradient-to-r from-emerald-800 via-teal-800 to-emerald-900 border-amber-405' :
            announcement.type === 'notice' ? 'bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 border-yellow-300' :
            announcement.type === 'danger' ? 'bg-gradient-to-r from-red-700 via-rose-800 to-red-800 border-zinc-200' :
            'bg-gradient-to-r from-zinc-900 via-indigo-950 to-zinc-900 border-amber-300'
          }`}
        >
          <div className="max-w-7xl mx-auto w-full flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 text-xs sm:text-xs text-left">
              <span className="shrink-0">
                {announcement.type === 'promo' && <Percent className="w-4 h-4 text-amber-300" />}
                {announcement.type === 'notice' && <Bell className="w-4 h-4 text-yellow-300" />}
                {announcement.type === 'danger' && <AlertTriangle className="w-4 h-4 text-white animate-pulse" />}
                {announcement.type === 'nightly' && <Sparkles className="w-4 h-4 text-amber-350" />}
              </span>
              <p className="font-semibold text-[11px] sm:text-xs leading-normal">
                {currentLanguage === 'uk' && announcement.messageUk
                  ? announcement.messageUk
                  : announcement.messageEn}
              </p>
            </div>
            
            {announcement.dismissible && (
              <button 
                onClick={() => setIsAnnouncementDismissed(true)}
                className="text-[9px] font-mono hover:bg-white/10 active:bg-white/20 px-2 py-1 rounded-lg border border-white/25 transition-all cursor-pointer shrink-0 ml-2"
                title="Dismiss Notice"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      <header className="sticky top-0 z-30 bg-zinc-950/95 backdrop-blur-md text-white shadow-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          
          <div className="flex items-center gap-3 cursor-pointer group logo brand" onClick={() => { navigateTo('home'); setIsMobileMenuOpen(false); }}>
            <div className="relative">
              <RobustImage 
                key={`logo-${imageVersion}`}
                mediaKey="logo"
                localPaths={['/cozzydine_logo.png', '/gallery/rooms/cozzydine_logo.png', '/logo.png', '/gallery/rooms/logo.png']}
                fallbackUnsplashUrl={cozzydineLogoMain}
                alt="Hostel Cozzydine Logo"
                width="44"
                height="44"
                className="w-11 h-11 rounded-full object-cover border-2 border-amber-400 p-[1px] shadow-md group-hover:rotate-12 transition-all duration-500"
                loading="eager"
              />
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-zinc-950 rounded-full" />
            </div>
            <div className="flex flex-col justify-center leading-[1.1] text-left select-none">
              <span className="text-[9px] font-bold tracking-[0.2em] text-amber-400 uppercase font-mono">{t('lblHostel') || 'Hostel'}</span>
              <span className="text-xl font-display font-bold tracking-tight text-white uppercase">Cozzydine</span>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-2 text-xs font-semibold font-mono tracking-wider nav-links desktop-only">
            <button 
              onClick={() => navigateTo('home')} 
              className={`px-4 py-2 rounded-xl transition-all duration-300 cursor-pointer ${activePage === 'home' ? 'bg-zinc-800 text-amber-300 font-extrabold' : 'text-zinc-350 hover:bg-zinc-900 hover:text-white'}`}
            >
              {t('navHome')}
            </button>
            <button 
              onClick={() => navigateTo('rooms')} 
              className={`px-4 py-2 rounded-xl transition-all duration-300 cursor-pointer ${activePage === 'rooms' ? 'bg-zinc-800 text-amber-300 font-extrabold' : 'text-zinc-350 hover:bg-zinc-900 hover:text-white'}`}
            >
              {t('navRooms')}
            </button>
            <button 
              onClick={() => navigateTo('about')} 
              className={`px-4 py-2 rounded-xl transition-all duration-300 cursor-pointer ${activePage === 'about' ? 'bg-zinc-800 text-amber-300 font-extrabold' : 'text-zinc-350 hover:bg-zinc-900 hover:text-white'}`}
            >
              {t('navAbout')}
            </button>
            <button 
              onClick={() => navigateTo('contact')} 
              className={`px-4 py-2 rounded-xl transition-all duration-300 cursor-pointer ${activePage === 'contact' ? 'bg-zinc-800 text-amber-300 font-extrabold' : 'text-zinc-350 hover:bg-zinc-900 hover:text-white'}`}
            >
              {t('navContact')}
            </button>
          </nav>

          <div className="hidden lg:flex items-center gap-4 utility-menu desktop-only">
            <React.Suspense fallback={<div className="w-20 h-10 bg-zinc-900 border border-zinc-800 rounded-xl animate-pulse"></div>}>
              <LanguageTranslator currentLanguage={currentLanguage} onLanguageChange={handleLanguageChange} />
            </React.Suspense>
            
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-amber-400 hover:bg-zinc-850 text-amber-300 hover:text-white transition-all cursor-pointer select-none shadow-sm shrink-0"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              id="theme-toggle-desktop"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400 shrink-0" /> : <Moon className="w-5 h-5 text-amber-400 shrink-0" />}
            </button>

            <button
               onClick={() => { setModalInitialStep(null); setModalInitialConfirmedBooking(null); navigateTo('rooms'); }}
               className="book-now-global"
            >
              {t('btnBook')}
            </button>
          </div>

          <div className="flex lg:hidden items-center gap-3 pr-4 mobile-only">
            <React.Suspense fallback={<div className="w-16 h-12 bg-zinc-900 border border-zinc-800 rounded-xl animate-pulse"></div>}>
              <LanguageTranslator currentLanguage={currentLanguage} onLanguageChange={handleLanguageChange} />
            </React.Suspense>

            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="flex items-center justify-center w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-amber-400 hover:bg-zinc-850 text-amber-300 hover:text-white transition-all cursor-pointer select-none shadow-sm shrink-0"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              id="theme-toggle-mobile"
            >
              {theme === 'dark' ? <Sun className="w-5.5 h-5.5 text-amber-400 shrink-0" /> : <Moon className="w-5.5 h-5.5 text-amber-400 shrink-0" />}
            </button>

            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex items-center justify-center w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-amber-400 hover:bg-zinc-850 text-amber-300 hover:text-white transition-all cursor-pointer select-none shadow-sm shrink-0"
              aria-label="Toggle Menu"
            >
              {isMobileMenuOpen ? <X className="w-6.5 h-6.5 animate-pulse" /> : <Menu className="w-6.5 h-6.5" />}
            </button>
          </div>

        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden absolute left-0 right-0 z-20 bg-zinc-950 border-t border-zinc-800 p-6 shadow-2xl flex flex-col gap-5 text-left transition-all duration-350 mobile-only-dropdown">
            <nav className="flex flex-col gap-2 text-xs font-semibold font-mono uppercase tracking-wider">
              <button 
                onClick={() => { navigateTo('home'); setIsMobileMenuOpen(false); }} 
                className={`px-4 py-3 rounded-xl text-left transition-all ${activePage === 'home' ? 'bg-zinc-900 text-amber-300 font-extrabold' : 'text-zinc-350 hover:bg-zinc-900/40'}`}
              >
                {t('navHome')}
              </button>
              <button 
                onClick={() => { navigateTo('rooms'); setIsMobileMenuOpen(false); }} 
                className={`px-4 py-3 rounded-xl text-left transition-all ${activePage === 'rooms' ? 'bg-zinc-900 text-amber-300 font-extrabold' : 'text-zinc-300 hover:bg-zinc-900/40'}`}
              >
                {t('navRooms')}
              </button>
              <button 
                onClick={() => { navigateTo('about'); setIsMobileMenuOpen(false); }} 
                className={`px-4 py-3 rounded-xl text-left transition-all ${activePage === 'about' ? 'bg-zinc-900 text-amber-300 font-extrabold' : 'text-zinc-300 hover:bg-zinc-900/40'}`}
              >
                {t('navAbout')}
              </button>
              <button 
                onClick={() => { navigateTo('contact'); setIsMobileMenuOpen(false); }} 
                className={`px-4 py-3 rounded-xl text-left transition-all ${activePage === 'contact' ? 'bg-zinc-900 text-amber-300 font-extrabold' : 'text-zinc-300 hover:bg-zinc-900/40'}`}
              >
                {t('navContact')}
              </button>
            </nav>
             <button
              onClick={() => { setModalInitialStep(null); setModalInitialConfirmedBooking(null); navigateTo('rooms'); setIsMobileMenuOpen(false); }}
              className="book-now-global text-center justify-center w-full"
            >
              {t('btnBook')}
            </button>
          </div>
        )}
      </header>

      <main className="transition-all duration-300">
        
        {activePage === 'home' && (
          <div className="space-y-6">
            
            <div className="max-w-7xl mx-auto px-6 pt-8">
              <RobustBackgroundSection
                key={`home-hero-${imageVersion}`}
                id="home-hero-section"
                mediaKey="home-hero"
                localPaths={[
                  '/images/home-hero.webp',
                  '/home-hero.webp'
                ]}
                fallbackUnsplashUrl={kyivMaidanAerial}
                className="relative overflow-hidden rounded-[2.5rem] bg-zinc-950 text-white py-24 px-8 sm:px-16 shadow-2xl border border-zinc-800 transition-all duration-300"
                overlayClassName="bg-gradient-to-r from-zinc-950/85 via-zinc-950/45 to-transparent"
              >
                <div className="absolute inset-0 bg-radial-at-t pointer-events-none z-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/85 via-zinc-950/35 to-transparent z-0 pointer-events-none" />
                
                <div className="relative z-10 max-w-3xl text-left space-y-8">

                  <h1 className="text-4xl sm:text-7xl font-display font-bold leading-[1.05] text-white tracking-tight drop-shadow-md">
                    <span className="block text-amber-400 text-xs sm:text-sm font-mono uppercase tracking-[0.22em] mb-4 font-black">Hostel Cozzydine Kyiv</span>
                    {t('heroTitlePart1')} <br />
                    {t('heroTitlePart2')}
                  </h1>

                  <p className="text-sm sm:text-base text-zinc-200 max-w-xl font-medium leading-relaxed">
                    {t('heroSubtitle')}
                  </p>

                  <div className="flex gap-4 pt-3">
                    <button
                      onClick={() => { setModalInitialStep(null); setModalInitialConfirmedBooking(null); navigateTo('rooms'); }}
                      className="book-now-global"
                    >
                      {t('btnBook') || 'Book Now'}
                    </button>
                    <a
                      href="tel:+380662070475"
                      className="px-8 py-4 bg-zinc-900/90 hover:bg-zinc-800 border border-zinc-700 text-white rounded-xl text-xs font-bold font-mono tracking-wider uppercase transition-all transform hover:-translate-y-0.5 duration-200 active:translate-y-0 cursor-pointer inline-flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4 text-amber-400" />
                      <span>{t('heroBtnContact') || 'CONTACT US'}</span>
                    </a>
                  </div>
                </div>
              </RobustBackgroundSection>
            </div>

            <div className="max-w-7xl mx-auto px-6 pt-4 pb-0">
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className={`rounded-[2rem] p-6 shadow-xl border transition-all duration-300 ${
                  theme === 'light'
                    ? 'bg-white border-zinc-200/80 shadow-zinc-200/50'
                    : 'bg-zinc-900 border-zinc-800 shadow-black/40'
                }`}
              >
                <form onSubmit={handleProceedBookingBar} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                  <div className="space-y-1.5 min-w-0">
                    <label className={`text-[10px] font-mono uppercase tracking-wider font-extrabold block ${theme === 'light' ? 'text-zinc-950' : 'text-zinc-400'}`}>
                      {t('checkIn')}
                    </label>
                    <div className="relative">
                      <Calendar className={`absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'light' ? 'text-zinc-800' : 'text-zinc-500'}`} />
                      <input
                        type="date"
                        value={barCheckIn}
                        min={todayStr}
                        onChange={(e) => {
                          setBarCheckIn(e.target.value);
                          if (barCheckOut && e.target.value >= barCheckOut) {
                            const nextDate = new Date(e.target.value);
                            nextDate.setDate(nextDate.getDate() + 1);
                            const y = nextDate.getFullYear();
                            const m = String(nextDate.getMonth() + 1).padStart(2, '0');
                            const d = String(nextDate.getDate()).padStart(2, '0');
                            setBarCheckOut(`${y}-${m}-${d}`);
                          }
                        }}
                        required
                        className={`w-full pl-10 pr-3 py-2.5 rounded-xl border font-mono text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none transition-all ${
                          theme === 'light'
                            ? 'bg-zinc-50 border-zinc-300 text-zinc-950 font-extrabold focus:bg-white'
                            : 'bg-zinc-950 border-zinc-800 text-white focus:bg-zinc-950'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5 min-w-0">
                    <label className={`text-[10px] font-mono uppercase tracking-wider font-extrabold block ${theme === 'light' ? 'text-zinc-950' : 'text-zinc-400'}`}>
                      {t('checkOut')}
                    </label>
                    <div className="relative">
                      <Calendar className={`absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'light' ? 'text-zinc-800' : 'text-zinc-500'}`} />
                      <input
                        type="date"
                        value={barCheckOut}
                        min={barCheckIn || todayStr}
                        onChange={(e) => setBarCheckOut(e.target.value)}
                        required
                        className={`w-full pl-10 pr-3 py-2.5 rounded-xl border font-mono text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none transition-all ${
                          theme === 'light'
                            ? 'bg-zinc-50 border-zinc-300 text-zinc-950 font-extrabold focus:bg-white'
                            : 'bg-zinc-950 border-zinc-800 text-white focus:bg-zinc-950'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5 min-w-0">
                    <label className={`text-[10px] font-mono uppercase tracking-wider font-extrabold block ${theme === 'light' ? 'text-zinc-950' : 'text-zinc-400'}`}>
                      {t('guests')}
                    </label>
                    <div className="relative">
                      <Users className={`absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'light' ? 'text-zinc-800' : 'text-zinc-500'}`} />
                      <select
                        value={barGuests}
                        onChange={(e) => setBarGuests(Number(e.target.value))}
                        className={`w-full pl-10 pr-8 py-2.5 rounded-xl border font-mono text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none transition-all appearance-none cursor-pointer ${
                          theme === 'light'
                            ? 'bg-zinc-50 border-zinc-300 text-zinc-950 font-extrabold focus:bg-white'
                            : 'bg-zinc-950 border-zinc-800 text-white focus:bg-zinc-950'
                        }`}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <option key={num} value={num} className={theme === 'dark' ? 'bg-zinc-950 text-white' : 'bg-white text-zinc-950 font-extrabold'}>
                            {num} {num === 1 ? (currentLanguage === 'uk' ? 'Гість' : 'Guest') : (currentLanguage === 'uk' ? 'Гості' : 'Guests')}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${theme === 'light' ? 'text-zinc-800' : 'text-zinc-500'}`} />
                    </div>
                  </div>

                  <div className="space-y-1.5 min-w-0">
                    <label className={`text-[10px] font-mono uppercase tracking-wider font-extrabold block ${theme === 'light' ? 'text-zinc-950' : 'text-zinc-400'}`}>
                      {t('roomType') || 'Room Type'}
                    </label>
                    <div className="relative">
                      <Lock className={`absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'light' ? 'text-zinc-800' : 'text-zinc-500'}`} />
                      <select
                        value={barRoomId}
                        onChange={(e) => setBarRoomId(e.target.value)}
                        className={`w-full pl-10 pr-8 py-2.5 rounded-xl border font-mono text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none transition-all appearance-none cursor-pointer ${
                          theme === 'light'
                            ? 'bg-zinc-50 border-zinc-300 text-zinc-950 font-extrabold focus:bg-white'
                            : 'bg-zinc-950 border-zinc-800 text-white focus:bg-zinc-950'
                        }`}
                      >
                        {fallbackRooms.map((room) => (
                          <option key={room.id} value={room.id} className={theme === 'dark' ? 'bg-zinc-950 text-white' : 'bg-white text-zinc-950 font-extrabold'}>
                            {t('room_' + room.id + '_name') || room.name} ({room.pricePerNight} UAH)
                          </option>
                        ))}
                      </select>
                      <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${theme === 'light' ? 'text-zinc-800' : 'text-zinc-500'}`} />
                    </div>
                  </div>

                  <div className="min-w-0">
                    <button
                      type="submit"
                      className="w-full py-3 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-350 hover:to-amber-450 active:scale-98 text-zinc-950 font-mono text-xs font-black uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <span>{t('btnBook') || 'Book Now'}</span>
                      <ArrowRight className="w-4 h-4 text-zinc-950" />
                    </button>
                  </div>
                </form>

                <div className="w-full flex justify-center mt-4 pt-3 border-t border-dashed border-blue-500/20">
                  <div className="transition-all duration-300 hover:scale-[1.01] flex items-center justify-center gap-2.5 max-w-2xl text-center w-full">
                    <span className="text-base inline-block animate-bounce">🏷️✨</span>
                    <p className="text-xs sm:text-sm font-black tracking-wide animate-pulse vl-prominent-blue">
                      {t('lblLongStayOfferDesc') || 'Book 3+ nights for a 10% discount, or 7+ nights to 15% off.'}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <React.Suspense fallback={<div className="w-full h-96 bg-zinc-900/50 rounded-3xl animate-pulse my-12" />}>
              <PhotoGallery theme={theme} currentLanguage={currentLanguage} t={t} />
            </React.Suspense>

            <div className="max-w-7xl mx-auto px-6 pt-16 pb-4">
              <section 
                id="facilities-grid"
                className={`p-8 sm:p-14 relative overflow-hidden rounded-[24px] transition-all duration-300 ${
                  theme === 'light' 
                    ? 'bg-white border border-zinc-200 shadow-sm' 
                    : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
                }`}
              >
                <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
                  <span className={`text-[10px] font-mono tracking-[0.25em] font-extrabold px-3.5 py-1.5 rounded-full uppercase inline-block ${
                    theme === 'light'
                      ? 'text-emerald-800 bg-emerald-50 border border-emerald-100/50'
                      : 'text-emerald-400 bg-emerald-950/40 border border-emerald-800/40'
                  }`}>
                    {t('amenitiesLabel')}
                  </span>
                  <h2 className={`cozzydine-amenities-title text-3xl sm:text-5xl font-display font-bold tracking-tight ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`}>{t('amenitiesTitle')}</h2>
                  <p className={`text-sm sm:text-base max-w-xl mx-auto leading-relaxed ${theme === 'light' ? 'text-zinc-550' : 'text-[#A8A29E]'}`}>{t('amenitiesDesc')}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { key: "amWifi", icon: Wifi, colorClass: "text-amber-500 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/40" },
                    { key: "amSecurity", icon: ShieldCheck, colorClass: "text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/40" },
                    { key: "amWater", icon: Droplet, colorClass: "text-blue-500 bg-blue-50 dark:bg-blue-950/30 border border-blue-200/40" },
                    { key: "amLockers", icon: Lock, colorClass: "text-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200/40" },
                    { key: "amLaundry", icon: Shirt, colorClass: "text-purple-500 bg-purple-50 dark:bg-purple-950/30 border border-purple-200/40" },
                    { key: "amAc", icon: Wind, colorClass: "text-sky-500 bg-sky-50 dark:bg-sky-950/30 border border-sky-200/40" },
                    { key: "amLuggage", icon: Briefcase, colorClass: "text-rose-500 bg-rose-50 dark:bg-rose-950/30 border border-rose-200/40" },
                    { key: "amReception", icon: Clock, colorClass: "text-teal-500 bg-teal-50 dark:bg-teal-950/30 border border-teal-200/40" }
                  ].map((item, idx) => {
                    const IconComponent = item.icon;
                    return (
                      <div 
                        id={`facility-${item.key}`} 
                        key={idx} 
                        className={`group p-5 rounded-[24px] flex items-center gap-4 transform hover:-translate-y-1 transition-all duration-300 ${
                          theme === 'light'
                            ? 'bg-white border border-zinc-200 shadow-xs hover:border-amber-400 hover:shadow-md'
                            : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md hover:scale-[1.02]'
                        }`}
                        style={theme === 'dark' ? { boxShadow: '0 8px 20px -4px rgba(0,0,0,0.4), 0 4px 6px -2px rgba(0,0,0,0.4)' } : undefined}
                      >
                        <div className={`shrink-0 w-12 h-12 rounded-[12px] flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-xs ${item.colorClass}`}>
                          <IconComponent className="w-6 h-6 stroke-[2]" />
                        </div>
                        <div className="text-left">
                          <h3 className={`font-display font-bold text-sm amenity-title ${theme === 'light' ? 'text-zinc-900' : 'text-[#FAFAFA]'}`}>{t(item.key)}</h3>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            <section id="area-guide" className={`py-14 transition-colors duration-300 ${theme === 'light' ? 'bg-slate-50 border-t border-zinc-200/60' : 'bg-transparent'}`}>
              <div className="max-w-7xl mx-auto px-6">
                <div className={`p-8 sm:p-14 relative overflow-hidden rounded-[24px] transition-all duration-300 ${
                  theme === 'light'
                    ? 'bg-white border border-zinc-200 shadow-sm'
                    : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
                }`}>
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                    <div className="max-w-2xl text-left space-y-4">
                      <span className={`text-[11px] font-mono tracking-[0.2em] font-extrabold px-3.5 py-1.5 rounded-full uppercase inline-block ${
                        theme === 'light'
                          ? 'text-emerald-800 bg-emerald-50 border border-emerald-100/55'
                          : 'text-emerald-400 bg-emerald-950/40 border border-emerald-800/40'
                      }`}>
                        🗺️ {t('sightseeingTag')}
                      </span>
                      <h2 className={`cozzydine-sightseeing-title text-3xl sm:text-4xl font-display font-bold tracking-tight ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`}>{t('sightseeingTitle')}</h2>
                      <p className={`text-sm sm:text-base max-w-xl leading-relaxed ${theme === 'light' ? 'text-zinc-600' : 'text-[#A8A29E]'}`}>
                        {t('sightseeingDesc')}
                      </p>
                    </div>
                  </div>

                  <div className="relative px-2 sm:px-14">
                    <button
                      onClick={() => {
                        const maxIdx = isMobile ? 6 : 4;
                        setSightseeingIndex(prev => (prev <= 0 ? maxIdx : prev - 1));
                      }}
                      className={`absolute left-1 sm:left-[-24px] top-1/2 -translate-y-1/2 z-20 w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 transition-all duration-300 shadow-xl flex items-center justify-center cursor-pointer group focus:outline-none focus:ring-4 ${
                        theme === 'light'
                          ? 'border-emerald-800/30 bg-white text-emerald-800 hover:bg-emerald-850 hover:border-emerald-850 hover:text-white focus:ring-emerald-800/20'
                          : 'border-emerald-700/40 bg-zinc-900 text-emerald-400 hover:bg-emerald-850 hover:border-emerald-850 hover:text-white focus:ring-emerald-500/20'
                      } hover:scale-105 active:scale-95`}
                      aria-label="Previous sight"
                      id="slide-arrow-left"
                    >
                      <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
                    </button>

                    <div className="relative overflow-hidden py-2">
                      <div 
                        className="flex transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${sightseeingIndex * (isMobile ? 100 : 33.333333)}%)` }}
                      >
                        {[
                          { name: t("sight1Name"), meters: t("sight1Meters"), desc: t("sight1Desc") },
                          { name: t("sight2Name"), meters: t("sight2Meters"), desc: t("sight2Desc") },
                          { name: t("sight3Name"), meters: t("sight3Meters"), desc: t("sight3Desc") },
                          { name: t("sight4Name"), meters: t("sight4Meters"), desc: t("sight4Desc") },
                          { name: t("sight5Name"), meters: t("sight5Meters"), desc: t("sight5Desc") },
                          { name: t("sight6Name"), meters: t("sight6Meters"), desc: t("sight6Desc") },
                          { name: t("sight7Name"), meters: t("sight7Meters"), desc: t("sight7Desc") }
                        ].map((spot, i) => (
                          <div 
                            key={i} 
                            className="w-full md:w-1/3 shrink-0 px-3"
                            itemScope
                            itemType="https://schema.org/TouristAttraction"
                            data-llm-landmark={spot.name}
                            data-llm-transit-time={spot.meters}
                            data-llm-origin="Hostel Cozzydine"
                          >
                            <div 
                              className={`p-8 rounded-[24px] h-full flex flex-col justify-between space-y-4 relative transform hover:-translate-y-1 transition-all duration-300 ${
                                theme === 'light'
                                  ? 'bg-zinc-50 border border-zinc-200 hover:border-amber-400 hover:bg-white shadow-2xs'
                                  : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-lg'
                              }`}
                              style={theme === 'dark' ? { boxShadow: '0 12px 24px -10px rgba(0,0,0,0.5), 0 4px 6px -4px rgba(0,0,0,0.4)' } : undefined}
                            >
                              <div className="space-y-3 text-left">
                                <h3 
                                  itemProp="name"
                                  className={`font-bold font-display vl-prominent-blue ${theme === 'light' ? 'font-extrabold' : ''} ${
                                    viewportScale < 0.65 ? 'text-3xl sm:text-4xl' : viewportScale < 1 ? 'text-2xl' : 'text-xl md:text-base'
                                  }`}
                                >
                                  {spot.name}
                                </h3>
                                <p 
                                  itemProp="description"
                                  className={`text-sm leading-relaxed ${theme === 'light' ? 'text-zinc-950 font-semibold' : 'text-[#A8A29E]'}`}
                                >
                                  {spot.desc}
                                </p>
                              </div>
                              <div className="text-left pt-2">
                                <span 
                                  itemProp="publicAccess" 
                                  content="true"
                                  className="hidden"
                                />
                                <span className={`inline-block text-xs font-mono font-bold px-3 py-1 rounded-lg ${
                                  theme === 'light'
                                    ? 'text-emerald-850 bg-emerald-100/60'
                                    : 'text-emerald-400 bg-emerald-950/40 border border-emerald-800/40'
                                }`}>
                                  {spot.meters}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        const maxIdx = isMobile ? 6 : 4;
                        setSightseeingIndex(prev => (prev >= maxIdx ? 0 : prev + 1));
                      }}
                      className={`absolute right-1 sm:right-[-24px] top-1/2 -translate-y-1/2 z-20 w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 transition-all duration-300 shadow-xl flex items-center justify-center cursor-pointer group focus:outline-none focus:ring-4 ${
                        theme === 'light'
                          ? 'border-emerald-800/30 bg-white text-emerald-800 hover:bg-emerald-850 hover:border-emerald-850 hover:text-white focus:ring-emerald-800/20'
                          : 'border-emerald-700/40 bg-zinc-900 text-emerald-400 hover:bg-emerald-850 hover:border-emerald-850 hover:text-white focus:ring-emerald-500/20'
                      } hover:scale-105 active:scale-95`}
                      aria-label="Next sight"
                      id="slide-arrow-right"
                    >
                      <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 group-hover:translate-x-0.5 transition-transform stroke-[2.5]" />
                    </button>
                  </div>

                  <div className="flex justify-center items-center gap-2 mt-8">
                    {Array.from({ length: (isMobile ? 7 : 5) }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setSightseeingIndex(i)}
                        className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                          sightseeingIndex === i 
                            ? (theme === 'light' ? "w-6 bg-emerald-800" : "w-6 bg-emerald-400")
                            : (theme === 'light' ? "w-2 bg-zinc-200 hover:bg-zinc-350" : "w-2 bg-zinc-700 hover:bg-zinc-600")
                        }`}
                        aria-label={`Go to slide ${i + 1}`}
                        id={`spot-dot-${i}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section id="transit-guide" className={`py-14 transition-colors duration-300 ${theme === 'light' ? 'bg-white border-t border-zinc-200/60' : 'bg-transparent'}`}>
              <div className="max-w-7xl mx-auto px-6">
                <div className={`p-8 sm:p-14 relative overflow-hidden rounded-[24px] transition-all duration-300 ${
                  theme === 'light'
                    ? 'bg-white border border-zinc-200 shadow-sm'
                    : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
                }`}>
                  <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                    <div className="space-y-3">
                      <span className={`text-xs font-mono tracking-[0.2em] font-extrabold px-3.5 py-1.5 rounded-full uppercase inline-block ${
                        theme === 'light' ? 'text-emerald-800 bg-emerald-50 border border-emerald-100/55' : 'text-emerald-400 bg-emerald-950/40 border border-emerald-800/40'
                      }`}>
                        🚏 {t('directionsTitle')}
                      </span>
                      <h2 className={`cozzydine-directions-title text-3xl sm:text-4xl font-display font-bold tracking-tight ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`}>{t('directionsTitle')}</h2>
                      <p className={`text-sm sm:text-base max-w-xl leading-relaxed ${theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'}`}>{t('directionsSubtitle')}</p>
                    </div>
                    <div className={`shrink-0 p-5 rounded-3xl text-left lg:text-right flex flex-col lg:items-end gap-3.5 max-w-md transition-all duration-300 ${
                      theme === 'light'
                        ? 'bg-zinc-50 border border-zinc-200'
                        : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md'
                    }`}>
                      <div>
                        <p className={`text-sm font-bold ${theme === 'light' ? 'text-zinc-900' : 'text-[#FAFAFA]'}`}>{tc.address}</p>
                        <p className={`text-xs font-mono font-bold mt-1 ${theme === 'light' ? 'text-emerald-800' : 'text-emerald-400'}`}>{tc.tel}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-zinc-200/60 w-full justify-start lg:justify-end">
                        <a 
                          href="https://wa.me/message/X2J2TJSJL2QYD1"
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Chat on WhatsApp"
                          id="transit-social-whatsapp"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.454 5.709 1.455h.008c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </a>
                        <a 
                          href="https://connect.viber.com/business/10ada262-b505-11ef-b999-ba8d589337c0"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-[#7360F2] hover:bg-[#7360F2] hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Chat on Viber"
                          id="transit-social-viber"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M21.365 1.554c-2.316-1.127-5.127-1.554-9.365-1.554-4.238 0-7.05.427-9.365 1.554C.913 2.385.034 4.09.006 6.315c-.052 4.1.353 7.82 2.668 10.957-.492 1.778-1.538 4.225-2.522 5.86-.345.57.172 1.272.795 1.054 2.42-1.077 5.564-2.83 7.158-3.663 1.2.3 2.515.427 3.895.427 4.238 0 7.05-.427 9.365-1.554C23.682 18.272 24 16.275 24 12c0-4.275-.318-6.272-2.635-7.446v-.001-.9-.1-.1l-.1-.2 M18.47 14.88c-.378.197-.84.288-1.385.275-.853-.016-1.745-.333-2.645-.945-1.637-1.11-2.92-2.37-3.957-3.91-.707-1.05-1.11-2.023-1.223-2.9-.064-.5.038-.97.3-1.4l.263-.44c.435-.724 1.135-.855 1.635-.453l1.196.963c.43.345.45.897.054 1.274l-.442.422c-.112.108-.13.262-.047.387.614 1.052 1.34 1.954 2.215 2.76.793.733 1.636 1.298 2.518 1.666.126.05.263.003.328-.1l.34-.482c.28-.403.793-.524 1.205-.27l1.314.808c.53.332.61.94.195 1.48L18.47 14.88z" />
                          </svg>
                        </a>
                        <a 
                          href="https://t.me/Cozzydine_Kyiv"
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-[#229ED9] hover:bg-[#229ED9] hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Contact on Telegram"
                          id="transit-social-telegram"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701-.332 4.981c.488 0 .704-.224.977-.487l2.35-2.285 4.887 3.61c.901.497 1.55.241 1.775-.838l3.205-15.101c.329-1.316-.5-1.913-1.362-1.524z"/>
                          </svg>
                        </a>
                        <a 
                          href="https://www.instagram.com/cozzydine_kyiv?igsh=ZzZnamhndzgwbDF2&utm_source=qr" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-[#ee2a7b] hover:bg-gradient-to-tr hover:from-[#f9ce34] hover:via-[#ee2a7b] hover:to-[#6228d7] hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Follow on Instagram"
                          id="transit-social-instagram"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.79-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                          </svg>
                        </a>
                        <a 
                          href="https://x.com/cozzydine" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-zinc-400 hover:bg-black hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Follow on X"
                          id="transit-social-x"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-8 border-b border-zinc-100 pb-5">
                    {[
                      { key: 'lybidska', label: t('tabLybidska') },
                      { key: 'bus', label: t('tabBus') },
                      { key: 'palats', label: t('tabPalats') },
                      { key: 'train', label: t('tabTrain') },
                      { key: 'centralBus', label: t('tabCentralBus') }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTransitTab(tab.key as any)}
                        className={`px-5 py-2.5 rounded-xl text-xs font-mono font-bold uppercase transition-all cursor-pointer ${
                          activeTransitTab === tab.key 
                            ? (theme === 'light' ? 'bg-zinc-950 text-white shadow-md' : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] text-[#FAFAFA] border-t border-[rgba(255,255,255,0.15)] shadow-md')
                            : (theme === 'light' ? 'bg-zinc-50 border border-zinc-200 text-zinc-500 hover:text-zinc-900' : 'bg-zinc-900/60 border border-zinc-800 text-zinc-400 hover:text-[#FAFAFA]')
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    <ol className="lg:col-span-7 space-y-4">
                      {(activeTransitTab === 'lybidska' ? tc.lybidskaSteps : activeTransitTab === 'bus' ? tc.busSteps : activeTransitTab === 'palats' ? tc.palatsSteps : activeTransitTab === 'centralBus' ? tc.centralBusSteps : tc.trainSteps).map((step, idx) => (
                        <li 
                          key={idx} 
                          className={`flex gap-4 items-start p-4 rounded-2xl mr-2 transition-all duration-300 ${
                            theme === 'light'
                              ? 'bg-zinc-50/75 border border-zinc-200 shadow-xs'
                              : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md'
                          }`}
                          style={theme === 'dark' ? { boxShadow: '0 8px 16px -4px rgba(0,0,0,0.4)' } : undefined}
                        >
                          <span className={`w-7 h-7 rounded-full text-xs font-mono font-black flex items-center justify-center shrink-0 ${
                            theme === 'light'
                              ? 'bg-emerald-50 border border-emerald-150 text-emerald-800'
                              : 'bg-emerald-950/40 border border-emerald-800/40 text-emerald-400'
                          }`}>
                            {idx + 1}
                          </span>
                          <p className={`text-sm sm:text-base leading-relaxed font-semibold text-left ${
                            theme === 'light' ? 'text-zinc-600' : 'text-[#FAFAFA]'
                          }`}>{step}</p>
                        </li>
                      ))}
                    </ol>

                    <div className={`transit-checklist-card lg:col-span-5 rounded-[24px] p-8 shadow-lg space-y-6 transition-all duration-300 ${
                      theme === 'light'
                        ? 'bg-gradient-to-br from-zinc-900 to-zinc-950 text-white'
                        : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.08)] text-white'
                    }`}>
                      <div className="flex items-center gap-2">
                        <Milestone className="w-5 h-5 text-emerald-500" />
                        <span className="text-xs font-mono font-bold uppercase tracking-wider transit-checklist-title">{t('checklistTitle')}</span>
                      </div>
                      <div className="space-y-4 font-mono text-xs text-zinc-300">
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpot2')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit250m') || '250 meters'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpotBus')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit300m') || '300 meters'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpotCentralBus')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit3m') || '3 mins ride'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpotRailway')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit10m') || '10 mins ride'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpot3')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit10m') || '10 mins ride'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <React.Suspense fallback={
              <div className="w-full h-[450px] bg-zinc-900/50 rounded-3xl border border-zinc-800 flex flex-col items-center justify-center gap-4 animate-pulse p-6">
                <Map className="w-12 h-12 text-zinc-600 animate-bounce" />
                <span className="text-sm text-zinc-500 font-mono">{t('lblLoadingMap') || 'Loading Cozzy Business Location Map...'}</span>
              </div>
            }>
              <CozzyBusinessMap currentLanguage={currentLanguage} />
            </React.Suspense>
          </div>
        )}

        {activePage === 'rooms' && (
          <div className="py-16 max-w-7xl mx-auto px-6 space-y-12">
            <RobustBackgroundSection
              key={`rooms-hero-${imageVersion}`}
              id="rooms-hero-section"
              mediaKey="rooms-hero"
              localPaths={[
                '/images/rooms-hero.webp',
                '/rooms-hero.webp'
              ]}
              fallbackUnsplashUrl={kyivMystetskyiArsenal}
              className="relative overflow-hidden rounded-[2.5rem] bg-zinc-950 text-white py-24 px-8 sm:px-16 text-left shadow-2xl border border-zinc-800 transition-all duration-300"
              overlayClassName="bg-gradient-to-r from-zinc-950/85 via-zinc-950/45 to-transparent"
            >
              <div className="absolute inset-0 bg-radial-at-t pointer-events-none z-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/85 via-zinc-950/35 to-transparent z-0 pointer-events-none" />
              
              <div className="relative z-10 max-w-3xl space-y-5">
                <h1 className="text-3xl sm:text-6xl font-display font-bold tracking-tight leading-none text-white drop-shadow-md">
                  {t('roomsAndDorms')}
                </h1>
                <p className="text-sm sm:text-base text-zinc-200 font-medium max-w-xl leading-relaxed">
                  {t('roomsSubtitle')}
                </p>
              </div>
            </RobustBackgroundSection>

            <BookingForm imageVersion={imageVersion} currentLanguage={currentLanguage} theme={theme} />

            <section 
              id="policies" 
              className={`py-20 mt-16 transition-all duration-300 rounded-[24px] ${
                theme === 'light'
                  ? 'bg-slate-50 border border-zinc-200 shadow-sm'
                  : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
              }`}
            >
              <div className="max-w-7xl mx-auto px-6">
                <div className="text-center max-w-3xl mx-auto space-y-3 mb-12">
                  <span className={`text-xs font-mono tracking-widest uppercase px-3.5 py-1.5 rounded-full font-black shadow-xs ${
                    theme === 'light'
                      ? 'text-emerald-800 bg-emerald-50 border border-emerald-200/50'
                      : 'text-emerald-400 bg-emerald-950/40 border border-emerald-800/40'
                  }`}>
                    📜 {t('policiesTitle')}
                  </span>
                  <h2 className={`text-3xl sm:text-4xl font-black tracking-tight ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`} id="policies-heading">{t('policiesTitle')}</h2>
                  <p className={`text-sm max-w-2xl mx-auto ${theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'}`}>{t('policiesSubtitle')}</p>
                </div>

                {(() => {
                  const policyItems = [
                    {
                      category: "stay",
                      icon: Clock,
                      colorClass: "text-amber-500 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/40",
                      titleKey: "policyCheckInOutTitle",
                      descKey: "policyCheckInOutDesc",
                      severity: "standard"
                    },
                    {
                      category: "stay",
                      icon: CreditCard,
                      colorClass: "text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/40",
                      titleKey: "policyPaymentTitle",
                      descKey: "policyPaymentDesc",
                      severity: "standard"
                    },
                    {
                      category: "stay",
                      icon: Calendar,
                      colorClass: "text-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200/40",
                      titleKey: "policyCancellationTitle",
                      descKey: "policyCancellationDesc",
                      severity: "standard"
                    },
                    {
                      category: "facilities",
                      icon: Briefcase,
                      colorClass: "text-rose-500 bg-rose-50 dark:bg-rose-950/30 border border-rose-200/40",
                      titleKey: "policyLuggageTitle",
                      descKey: "policyLuggageDesc",
                      severity: "standard"
                    },
                    {
                      category: "rules",
                      icon: Moon,
                      colorClass: "text-blue-500 bg-blue-50 dark:bg-blue-950/30 border border-blue-200/40",
                      titleKey: "policyQuietTitle",
                      descKey: "policyQuietDesc",
                      severity: "attention"
                    },
                    {
                      category: "facilities",
                      icon: Shirt,
                      colorClass: "text-purple-500 bg-purple-50 dark:bg-purple-950/30 border border-purple-200/40",
                      titleKey: "policyLaundryTitle",
                      descKey: "policyLaundryDesc",
                      severity: "standard"
                    },
                    {
                      category: "rules",
                      icon: Beer,
                      isAlcoholProhibited: true,
                      colorClass: "text-red-500 bg-red-50 dark:bg-red-950/30 border border-red-200/40",
                      titleKey: "policyComfortTitle",
                      descKey: "policyComfortDesc",
                      severity: "zero-tolerance"
                    },
                    {
                      category: "rules",
                      icon: UserX,
                      colorClass: "text-orange-500 bg-orange-50 dark:bg-orange-950/30 border border-orange-200/40",
                      titleKey: "policyVisitorsTitle",
                      descKey: "policyVisitorsDesc",
                      severity: "restricted"
                    },
                    {
                      category: "rules",
                      icon: Coffee,
                      colorClass: "text-yellow-500 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200/40",
                      titleKey: "policyFoodTitle",
                      descKey: "policyFoodDesc",
                      severity: "restricted"
                    },
                    {
                      category: "facilities",
                      icon: Droplet,
                      colorClass: "text-teal-500 bg-teal-50 dark:bg-teal-950/30 border border-teal-200/40",
                      titleKey: "policyKitchenTitle",
                      descKey: "policyKitchenDesc",
                      severity: "standard"
                    }
                  ];

                  return (
                    <>
                      <div className={`flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mb-8 p-1.5 rounded-2xl max-w-2xl mx-auto transition-all duration-300 ${
                        theme === 'light'
                          ? 'bg-zinc-150/60 border border-zinc-200/50'
                          : 'bg-zinc-900 border border-zinc-800'
                      }`}>
                        {[
                          { id: 'all', labelKey: 'policyCatAll' },
                          { id: 'stay', labelKey: 'policyCatStay' },
                          { id: 'facilities', labelKey: 'policyCatFacilities' },
                          { id: 'rules', labelKey: 'policyCatRules' },
                        ].map((tab) => {
                          const isActive = selectedPolicyCategory === tab.id;
                          const count = tab.id === 'all' 
                            ? policyItems.length 
                            : policyItems.filter(p => p.category === tab.id).length;

                          return (
                            <button
                              key={tab.id}
                              onClick={() => setSelectedPolicyCategory(tab.id as any)}
                              className={`px-3.5 py-1.8 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 ${
                                isActive 
                                  ? (theme === 'light' ? 'bg-white text-emerald-900 shadow-sm border border-zinc-200/60' : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] text-amber-300 border border-[rgba(255,255,255,0.15)] shadow-md')
                                  : (theme === 'light' ? 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200/40' : 'text-zinc-400 hover:text-[#FAFAFA]')
                              }`}
                            >
                              {t(tab.labelKey)}
                              <span className={`text-[9.5px] font-mono px-1.5 py-0.2 rounded-md transition-colors ${
                                isActive 
                                  ? (theme === 'light' ? 'bg-emerald-50 text-emerald-800 font-extrabold' : 'bg-emerald-950/40 text-emerald-400') 
                                  : (theme === 'light' ? 'bg-zinc-200/70 text-zinc-500' : 'bg-zinc-800 text-zinc-400')
                              }`}>
                                {count}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      <div className="relative min-h-[300px]">
                        <motion.div 
                          layout="position"
                          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
                        >
                          <AnimatePresence mode="popLayout">
                            {policyItems
                              .filter(p => selectedPolicyCategory === 'all' || p.category === selectedPolicyCategory)
                              .map((policy, idx) => {
                                let severityBadge = null;

                                if (policy.severity === "zero-tolerance") {
                                  severityBadge = (
                                    <span className={`text-[9px] font-mono font-black px-2 py-0.5 rounded-md uppercase tracking-wide ${
                                      theme === 'light'
                                        ? 'text-rose-700 bg-rose-50 border border-rose-200'
                                        : 'text-rose-400 bg-rose-950/40 border border-rose-900/40'
                                    }`}>
                                      🚨 Prohibited
                                    </span>
                                  );
                                } else if (policy.severity === "restricted") {
                                  severityBadge = (
                                    <span className={`text-[9px] font-mono font-black px-2 py-0.5 rounded-md uppercase tracking-wide ${
                                      theme === 'light'
                                        ? 'text-orange-700 bg-orange-50 border border-orange-100'
                                        : 'text-orange-400 bg-orange-950/40 border border-orange-900/40'
                                    }`}>
                                      🚫 Prohibited
                                    </span>
                                  );
                                } else if (policy.severity === "attention") {
                                  severityBadge = (
                                    <span className={`text-[9px] font-mono font-black px-2 py-0.5 rounded-md uppercase tracking-wide ${
                                      theme === 'light'
                                        ? 'text-amber-700 bg-amber-50 border border-amber-150'
                                        : 'text-amber-400 bg-amber-950/40 border border-amber-900/40'
                                    }`}>
                                      ⚠️ Guideline
                                    </span>
                                  );
                                } else {
                                  severityBadge = (
                                    <span className={`text-[9px] font-mono font-black px-2 py-0.5 rounded-md uppercase tracking-wide ${
                                      theme === 'light'
                                        ? 'text-emerald-850 bg-emerald-50 border border-emerald-100'
                                        : 'text-emerald-400 bg-emerald-950/40 border border-emerald-900/40'
                                    }`}>
                                      ✓ Standard
                                    </span>
                                  );
                                }

                                 return (
                                  <motion.div 
                                    layout
                                    initial={{ opacity: 0, scale: 0.97 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.97 }}
                                    transition={{ duration: 0.22, ease: "easeOut" }}
                                    key={policy.titleKey}
                                    className={`group flex gap-3.5 p-4 rounded-2xl transition-all duration-300 text-left items-start cursor-pointer hover:border-amber-400 ${
                                      theme === 'light'
                                        ? 'bg-white border border-zinc-200 hover:shadow-md'
                                        : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md hover:shadow-lg'
                                    }`}
                                    style={theme === 'dark' ? { boxShadow: '0 8px 16px -4px rgba(0,0,0,0.4)' } : undefined}
                                    id={`policy-item-${policy.titleKey}`}
                                  >
                                    {(() => {
                                      const IconComponent = policy.icon;
                                      if (policy.isAlcoholProhibited) {
                                        return (
                                          <div className={`shrink-0 w-12 h-12 rounded-[12px] flex items-center justify-center transition-all duration-300 group-hover:scale-110 shadow-xs relative ${policy.colorClass}`}>
                                            <IconComponent className="w-5 h-5 stroke-[2] opacity-75" />
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                              <Ban className="w-7 h-7 text-red-500/90 stroke-[2.5]" />
                                            </div>
                                          </div>
                                        );
                                      }
                                      return (
                                        <div className={`shrink-0 w-12 h-12 rounded-[12px] flex items-center justify-center transition-all duration-300 group-hover:scale-110 shadow-xs ${policy.colorClass}`}>
                                          <IconComponent className="w-6 h-6 stroke-[2]" />
                                        </div>
                                      );
                                    })()}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-1.5 flex-wrap mb-1">
                                        <h3 className={`font-extrabold text-sm font-display leading-tight ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`}>
                                          {t(policy.titleKey)}
                                        </h3>
                                        {severityBadge}
                                      </div>
                                      <div className={`text-xs sm:text-sm font-medium leading-relaxed whitespace-pre-line mt-1 ${theme === 'light' ? 'text-zinc-550' : 'text-[#A8A29E]'}`}>
                                        {t(policy.descKey)}
                                      </div>
                                    </div>
                                  </motion.div>
                                );
                              })}
                          </AnimatePresence>
                        </motion.div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </section>
          </div>
        )}

        {activePage === 'about' && (
          <div className="py-16 max-w-5xl mx-auto px-6 space-y-20">
            
            <RobustBackgroundSection
              key={`about-hero-${imageVersion}`}
              id="about-hero-section"
              mediaKey="about-hero"
              localPaths={[
                'custom-crimson-hexagon'
              ]}
              fallbackUnsplashUrl="custom-crimson-hexagon"
              className="relative overflow-hidden rounded-[2.5rem] bg-zinc-950 text-white py-28 px-8 sm:px-16 text-center shadow-2xl border border-zinc-800 transition-all duration-300"
              overlayClassName="bg-gradient-to-r from-zinc-950/85 via-zinc-950/45 to-transparent"
            >
              <div className="absolute inset-0 bg-radial-at-t pointer-events-none z-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/85 via-zinc-950/35 to-transparent z-0 pointer-events-none" />
              
              <div className="relative z-10 max-w-3xl mx-auto space-y-6 pb-4">
                <div className="flex justify-center mb-6">
                  <RobustImage 
                    key={`logo-about-${imageVersion}`}
                    mediaKey="logo-about"
                    localPaths={['/cozzydine_logo.png', '/gallery/rooms/cozzydine_logo.png', '/logo.png', '/gallery/rooms/logo.png']}
                    fallbackUnsplashUrl={cozzydineLogoMain}
                    alt="Hostel Cozzydine Large Logo" 
                    width="112"
                    height="112"
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-[2rem] object-cover bg-zinc-900/60 backdrop-blur-md border border-zinc-700/50 p-2.5 shadow-2xl hover:rotate-6 transition-transform duration-500" 
                    loading="lazy"
                  />
                </div>
                <h1 className="text-3xl sm:text-6xl font-display font-bold leading-normal text-white drop-shadow-md pb-4">
                  {t('navAbout')}
                </h1>
              </div>
            </RobustBackgroundSection>

            <section 
              className={`p-8 sm:p-14 relative overflow-hidden rounded-[24px] transition-all duration-300 ${
                theme === 'light'
                  ? 'bg-zinc-50 border border-zinc-200/60 shadow-sm'
                  : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-6 text-left">
                  <span className={`text-xs font-mono tracking-[0.25em] font-extrabold px-3.5 py-1.5 rounded-full uppercase inline-block ${
                    theme === 'light'
                      ? 'text-emerald-800 bg-emerald-50 border border-emerald-100/50'
                      : 'text-emerald-400 bg-emerald-950/40 border border-emerald-800/40'
                  }`}>
                    {t('navAbout')}
                  </span>
                  <div className="space-y-4">
                    {t('aboutText').split('\n\n').map((paragraph, index) => (
                      <p key={index} className={`text-sm sm:text-base leading-relaxed font-semibold ${theme === 'light' ? 'text-zinc-600' : 'text-[#FAFAFA]'}`}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
                <div className={`relative flex justify-center py-6 rounded-[2rem] p-6 shadow-sm transition-all duration-300 ${
                  theme === 'light'
                    ? 'bg-zinc-50 border border-zinc-200/60'
                    : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md'
                }`}>
                  <RobustImage
                    key={`about-reception-${imageVersion}`}
                    mediaKey="about-reception"
                    localPaths={[
                      '/cozzydine_logo_about us.jpg',
                      '/gallery/rooms/cozzydine_logo_about us.jpg',
                      '/cozzydine_logo_about%20us.jpg',
                      '/gallery/rooms/cozzydine_logo_about%20us.jpg'
                    ]}
                    fallbackUnsplashUrl={cozzydineReceptionMain}
                    alt="Hostel Cozzydine Reception"
                    className="rounded-2xl object-cover max-h-[340px] w-auto shadow-md border border-zinc-200"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            <section 
              id="reviews-grid-section" 
              className={`p-8 sm:p-14 relative overflow-hidden rounded-[24px] transition-all duration-300 ${
                theme === 'light'
                  ? 'bg-zinc-50/50 border border-zinc-200 shadow-sm'
                  : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
              }`}
            >
              <div className="max-w-xl space-y-3 mb-10 text-left">
                <span className={`text-xs font-mono tracking-[0.2em] font-bold px-3.5 py-1.5 rounded-full uppercase inline-block ${
                  theme === 'light'
                    ? 'text-emerald-800 bg-emerald-50 border border-emerald-100/60'
                    : 'text-emerald-400 bg-emerald-950/40 border border-emerald-800/40'
                }`}>
                  {t('testimonialsTag')}
                </span>
                <h2 className={`text-3xl sm:text-4xl font-display font-bold tracking-tight ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`}>{t('testimonialsTitle')}</h2>
                <p className={`text-sm sm:text-base max-w-lg leading-relaxed ${theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'}`}>{t('testimonialsDesc')}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fallbackReviews.map((rev) => (
                  <div 
                    key={rev.id} 
                    className={`rounded-3xl p-6 shadow-2xs relative flex flex-col justify-between space-y-5 hover:border-amber-400 group transform hover:-translate-y-0.5 transition-all duration-300 ${
                      theme === 'light'
                        ? 'bg-white border border-zinc-200 hover:shadow-md'
                        : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md hover:shadow-lg'
                    }`}
                    style={theme === 'dark' ? { boxShadow: '0 8px 16px -4px rgba(0,0,0,0.4)' } : undefined}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-full overflow-hidden shrink-0 border bg-amber-50 flex items-center justify-center p-0.5 shadow-inner ${
                            theme === 'light' ? 'border-zinc-200' : 'border-zinc-700/50'
                          }`}>
                            <img 
                              src={rev.avatar} 
                              alt={rev.author} 
                              width="44"
                              height="44"
                              className="w-full h-full rounded-full object-cover" 
                              referrerPolicy="no-referrer"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <div>
                            <h3 className={`font-bold text-sm tracking-tight ${theme === 'light' ? 'text-zinc-900' : 'text-[#FAFAFA]'}`}>{rev.author}</h3>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 text-amber-500">
                          {Array.from({ length: rev.rating }).map((_, i) => (
                            <Star key={i} className="w-3.5 h-3.5 fill-current text-amber-400" />
                          ))}
                        </div>
                      </div>
                      <p className={`text-sm leading-relaxed font-semibold italic ${theme === 'light' ? 'text-zinc-650' : 'text-[#E5E5E5]'}`}>"{translations[currentLanguage]?.[`rev_${rev.id}_content`] || rev.content}"</p>
                    </div>

                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {rev.tags.map((tg, i) => {
                        const translatedTag = translations[currentLanguage]?.[`rev_${rev.id}_tag${i+1}`] || tg;
                        return (
                          <span 
                            key={i} 
                            className={`text-[11.5px] font-mono font-bold px-2.5 py-1 rounded-lg border ${
                              theme === 'light'
                                ? 'text-emerald-800 bg-emerald-50 border-emerald-100/35'
                                : 'text-emerald-400 bg-emerald-950/40 border-emerald-900/30'
                            }`}
                          >
                            ✓ {translatedTag}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section 
              id="faqs-accordion-section" 
              className={`p-8 sm:p-14 relative overflow-hidden rounded-[24px] transition-all duration-300 ${
                theme === 'light'
                  ? 'bg-zinc-50/50 border border-zinc-200 shadow-sm'
                  : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
              }`}
            >
              <React.Suspense fallback={<div className="w-full h-48 bg-zinc-900/40 rounded-2xl animate-pulse" />}>
                <FaqsPolicies currentLanguage={currentLanguage} theme={theme} />
              </React.Suspense>
            </section>

          </div>
        )}

        {activePage === 'contact' && (
          <>
            <div className="py-16 max-w-6xl mx-auto px-6 space-y-16">
            
              <RobustBackgroundSection
                key={`contact-hero-${imageVersion}`}
                id="contact-hero-section"
                mediaKey="contact-hero"
                localPaths={[
                  '/images/contact-hero.webp',
                  '/contact-hero.webp'
                ]}
                fallbackUnsplashUrl={kyivSquat17b}
                className="relative overflow-hidden rounded-[2.5rem] bg-zinc-950 text-white py-24 px-8 sm:px-16 text-left shadow-2xl border border-zinc-800 transition-all duration-300"
                overlayClassName="bg-gradient-to-r from-zinc-950/85 via-zinc-950/45 to-transparent"
              >
                <div className="absolute inset-0 bg-radial-at-t pointer-events-none z-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/85 via-zinc-950/35 to-transparent z-0 pointer-events-none" />
                
                <div className="relative z-10 max-w-3xl space-y-5">
                  <h1 className="text-3xl sm:text-6xl font-display font-bold tracking-tight leading-none text-white drop-shadow-md">
                    {t('contactTitle')}
                  </h1>
                  <p className="text-zinc-200 text-sm sm:text-base max-w-xl font-medium leading-relaxed">
                    {t('contactSubtitle')}
                  </p>
                </div>
              </RobustBackgroundSection>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
                <div className={`lg:col-span-7 rounded-3xl p-8 sm:p-10 transition-all duration-300 ${
                  theme === 'light'
                    ? 'bg-white border border-zinc-200 shadow-2xs'
                    : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
                }`}>
                  <h2 className={`font-display font-bold text-xl mb-6 ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`}>{t('contactFormTitle')}</h2>
                  {contactSubmitted ? (
                    <div className="py-12 text-center flex flex-col items-center justify-center gap-5">
                      <div className="bg-emerald-100 dark:bg-emerald-950/40 p-4 rounded-full text-emerald-800 dark:text-emerald-400 animate-pulse">
                        <CheckCircle className="w-10 h-10" />
                      </div>
                      <div className="space-y-2">
                        <h3 className={`font-display font-bold text-xl ${theme === 'light' ? 'text-emerald-950' : 'text-emerald-400'}`}>{t('contactSubmittedTitle')}</h3>
                        <p className={`text-sm max-w-md mx-auto leading-relaxed ${theme === 'light' ? 'text-zinc-400' : 'text-[#A8A29E]'}`}>{t('contactSubmittedDesc')}</p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} className="space-y-5 text-left">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="text-left">
                          <label className={`text-xs font-mono uppercase font-bold tracking-wider ${theme === 'light' ? 'text-zinc-400' : 'text-[#A8A29E]'}`}>{t('contactLblName')}</label>
                          <input
                            type="text"
                            name="contactName"
                            required
                            value={contactName}
                            onChange={handleContactFormChange}
                            placeholder="e.g. Liam Sterling"
                            className={`w-full mt-2 border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50 font-semibold transition-all ${
                              theme === 'light'
                                ? 'bg-zinc-50 border-zinc-200 text-zinc-950'
                                : 'bg-[#141210] border-zinc-800 text-[#F7F5F0]'
                            }`}
                          />
                        </div>
                        <div className="text-left">
                          <label className={`text-xs font-mono uppercase font-bold tracking-wider ${theme === 'light' ? 'text-zinc-400' : 'text-[#A8A29E]'}`}>{t('contactLblEmail')}</label>
                          <input
                            type="email"
                            name="contactEmail"
                            required
                            value={contactEmail}
                            onChange={handleContactFormChange}
                            placeholder="liam@domain.com"
                            className={`w-full mt-2 border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50 font-semibold transition-all ${
                              theme === 'light'
                                ? 'bg-zinc-50 border-zinc-200 text-zinc-950'
                                : 'bg-[#141210] border-zinc-800 text-[#F7F5F0]'
                            }`}
                          />
                        </div>
                      </div>

                      <div className="text-left">
                        <label className={`text-xs font-mono uppercase font-bold tracking-wider ${theme === 'light' ? 'text-zinc-400' : 'text-[#A8A29E]'}`}>{t('contactLblMsg')}</label>
                        <textarea
                          name="contactMessage"
                          required
                          rows={4}
                          value={contactMessage}
                          onChange={handleContactFormChange}
                          placeholder="Detail check-in timelines, private group reservations interest..."
                          className={`w-full mt-2 border rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50 font-semibold transition-all ${
                            theme === 'light'
                              ? 'bg-zinc-50 border-zinc-200 text-zinc-950'
                              : 'bg-[#141210] border-zinc-800 text-[#F7F5F0]'
                          }`}
                        />
                      </div>

                      <div className="pt-2 flex justify-end">
                        <button
                          type="submit"
                          className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-zinc-950 hover:bg-zinc-850 text-white rounded-xl text-xs font-bold font-mono tracking-wider uppercase transition-all transform hover:-translate-y-0.5 duration-200 active:translate-y-0 shadow-md cursor-pointer"
                        >
                          <Send className="w-4 h-4 text-amber-400" />
                          <span>{t('contactBtnSubmit')}</span>
                        </button>
                      </div>
                    </form>
                  )}
                </div>

                <div className="lg:col-span-5 space-y-6 text-left">
                  <div className={`contact-card-info rounded-3xl p-6 transition-all duration-300 space-y-4 ${
                    theme === 'light'
                      ? 'bg-zinc-950 border border-zinc-800 text-white shadow-xl'
                      : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] text-[#FAFAFA] shadow-md'
                  }`} style={{ boxShadow: '0 8px 16px -4px rgba(0,0,0,0.4)' }}>
                    <span className="text-[10.5px] font-mono tracking-widest uppercase font-bold block mb-2 contact-socials-title">{t('physicalEndpointLabel')}</span>
                    <div>
                      <h2 className="font-bold text-lg text-white">Hostel Cozzydine Kyiv</h2>
                      <p className="text-sm font-mono mt-1 text-zinc-300">{physicalAddress}</p>
                    </div>
                    <div className="h-px bg-zinc-800" />
                    <div className="text-sm font-mono space-y-3.5">
                      <div className="flex flex-row justify-between items-center flex-wrap gap-x-4 gap-y-1.5 border-b border-zinc-800/30 pb-2">
                        <span className="text-zinc-400 shrink-0 text-xs sm:text-sm">Email:</span>
                        <a href={`mailto:${emailAddress}`} className="underline font-bold contact-yellow-link contact-email-link break-all text-right text-xs sm:text-sm">{emailAddress}</a>
                      </div>
                      <div className="flex flex-row justify-between items-center flex-wrap gap-x-4 gap-y-1.5 border-b border-zinc-800/30 pb-2">
                        <span className="text-zinc-400 shrink-0 text-xs sm:text-sm">Call (Front Desk):</span>
                        <a href={`tel:${phoneNumber}`} className="underline font-bold contact-yellow-link whitespace-nowrap text-right text-xs sm:text-sm">{phoneNumber}</a>
                      </div>
                      <div className="flex flex-row justify-between items-center flex-wrap gap-x-4 gap-y-1.5 pb-0.5">
                        <span className="text-zinc-400 shrink-0 text-xs sm:text-sm">{t('workingHoursLabel')}</span>
                        <span className="text-zinc-100 text-right font-medium text-xs sm:text-sm">{t('workingHoursValue')}</span>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-zinc-800/80">
                      <span className="text-[9px] font-mono tracking-widest block mb-2.5 uppercase contact-socials-title">{t('directChatsLabel')}</span>
                      <div className="flex flex-wrap items-center gap-2.5">
                        <a 
                          href="https://wa.me/message/X2J2TJSJL2QYD1"
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-800/60 hover:bg-[#25D366] border border-zinc-700/40 hover:border-[#25D366] transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Chat on WhatsApp"
                          id="social-whatsapp"
                        >
                          <svg className="w-5.5 h-5.5 text-[#25D366] group-hover:text-white fill-current transition-colors duration-300" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.454 5.709 1.455h.008c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </a>

                        <a 
                          href="https://connect.viber.com/business/10ada262-b505-11ef-b999-ba8d589337c0"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-800/60 hover:bg-[#7360F2] border border-zinc-700/40 hover:border-[#7360F2] transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Chat on Viber"
                          id="social-viber"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg className="w-5.5 h-5.5 text-[#7360F2] group-hover:text-white fill-current transition-colors duration-300" viewBox="0 0 24 24">
                            <path d="M21.365 1.554c-2.316-1.127-5.127-1.554-9.365-1.554-4.238 0-7.05.427-9.365 1.554C.913 2.385.034 4.09.006 6.315c-.052 4.1.353 7.82 2.668 10.957-.492 1.778-1.538 4.225-2.522 5.86-.345.57.172 1.272.795 1.054 2.42-1.077 5.564-2.83 7.158-3.663 1.2.3 2.515.427 3.895.427 4.238 0 7.05-.427 9.365-1.554C23.682 18.272 24 16.275 24 12c0-4.275-.318-6.272-2.635-7.446v-.001-.9-.1-.1l-.1-.2 M18.47 14.88c-.378.197-.84.288-1.385.275-.853-.016-1.745-.333-2.645-.945-1.637-1.11-2.92-2.37-3.957-3.91-.707-1.05-1.11-2.023-1.223-2.9-.064-.5.038-.97.3-1.4l.263-.44c.435-.724 1.135-.855 1.635-.453l1.196.963c.43.345.45.897.054 1.274l-.442.422c-.112.108-.13.262-.047.387.614 1.052 1.34 1.954 2.215 2.76.793.733 1.636 1.298 2.518 1.666.126.05.263.003.328-.1l.34-.482c.28-.403.793-.524 1.205-.27l1.314.808c.53.332.61.94.195 1.48L18.47 14.88z" />
                          </svg>
                        </a>

                        <a 
                          href="https://t.me/Cozzydine_Kyiv"
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-800/60 hover:bg-[#229ED9] border border-zinc-700/40 hover:border-[#229ED9] transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Contact on Telegram"
                          id="social-telegram"
                        >
                          <svg className="w-5.5 h-5.5 text-[#229ED9] group-hover:text-white fill-current transition-colors duration-300" viewBox="0 0 24 24">
                            <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701-.332 4.981c.488 0 .704-.224.977-.487l2.35-2.285 4.887 3.61c.901.497 1.55.241 1.775-.838l3.205-15.101c.329-1.316-.5-1.913-1.362-1.524z"/>
                          </svg>
                        </a>

                        <a 
                          href="https://www.instagram.com/cozzydine_kyiv?igsh=ZzZnamhndzgwbDF2&utm_source=qr" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-800/60 hover:bg-gradient-to-tr hover:from-[#f9ce34] hover:via-[#ee2a7b] hover:to-[#6228d7] border border-zinc-700/40 hover:border-transparent transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Follow on Instagram"
                          id="social-instagram"
                        >
                          <svg className="w-5.5 h-5.5 text-pink-400 group-hover:text-white fill-current transition-colors duration-300" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                          </svg>
                        </a>

                        <a 
                          href="https://x.com/cozzydine" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-800/60 hover:bg-black border border-zinc-700/40 hover:border-black transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Follow on X"
                          id="social-x"
                        >
                          <svg className="w-5.5 h-5.5 text-zinc-300 group-hover:text-white fill-current transition-colors duration-300" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-3xl p-6 space-y-3 shadow-inner border transition-all duration-300 ${
                    theme === 'light'
                      ? 'bg-zinc-950 border-zinc-800 text-white shadow-xl'
                      : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] text-[#FAFAFA] shadow-md'
                  }`}>
                    <span className="text-[10px] font-mono uppercase tracking-wider font-bold block contact-metro-title">{t('metroAccessTitle')}</span>
                    <p className={`text-xs leading-normal ${theme === 'light' ? 'text-zinc-300' : 'text-zinc-400'}`}>
                      {t('metroAccessDesc')}
                    </p>
                    <ul className={`text-xs font-mono space-y-1.5 pl-4 list-disc ${theme === 'light' ? 'text-zinc-300' : 'text-zinc-400'}`}>
                      <li>{t('metroAccessItem1')}</li>
                      <li>{t('metroAccessItem2')}</li>
                      <li>{t('metroAccessItem3')}</li>
                    </ul>
                  </div>
                </div>

              </div>

            </div>

            <section id="contact-transit-guide-section" className={`py-14 transition-colors duration-300 ${theme === 'light' ? 'bg-white border-t border-zinc-200/60' : 'bg-transparent'}`}>
              <div className="max-w-7xl mx-auto px-6">
                <div 
                  id="contact-transit-guide" 
                  className={`p-8 sm:p-14 relative overflow-hidden rounded-[24px] transition-all duration-300 ${
                    theme === 'light'
                      ? 'bg-white border border-zinc-200 shadow-sm'
                      : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                    <div className="space-y-3 text-left">
                      <span className={`text-xs font-mono tracking-[0.2em] font-extrabold px-3.5 py-1.5 rounded-full uppercase inline-block ${
                        theme === 'light' ? 'text-emerald-800 bg-emerald-50 border border-emerald-100/55' : 'text-emerald-400 bg-emerald-950/40 border border-emerald-800/40'
                      }`}>
                        🚏 {t('directionsTitle')}
                      </span>
                      <h2 className={`cozzydine-directions-title text-3xl sm:text-4xl font-display font-bold tracking-tight ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`}>{t('directionsTitle')}</h2>
                      <p className={`text-sm sm:text-base max-w-xl leading-relaxed ${theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'}`}>{t('directionsSubtitle')}</p>
                    </div>
                    <div className={`shrink-0 p-5 rounded-3xl text-left lg:text-right flex flex-col lg:items-end gap-3.5 max-w-md transition-all duration-300 ${
                      theme === 'light'
                        ? 'bg-zinc-50 border border-zinc-200'
                        : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md'
                    }`}>
                      <div>
                        <p className={`text-sm font-bold ${theme === 'light' ? 'text-zinc-900' : 'text-[#FAFAFA]'}`}>{tc.address}</p>
                        <p className={`text-xs font-mono font-bold mt-1 ${theme === 'light' ? 'text-emerald-800' : 'text-emerald-400'}`}>{tc.tel}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-zinc-200/60 w-full justify-start lg:justify-end">
                        <a 
                          href="https://wa.me/message/X2J2TJSJL2QYD1"
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Chat on WhatsApp"
                          id="contact-transit-social-whatsapp"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.454 5.709 1.455h.008c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </a>
                        <a 
                          href="https://connect.viber.com/business/10ada262-b505-11ef-b999-ba8d589337c0"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-[#7360F2] hover:bg-[#7360F2] hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Chat on Viber"
                          id="contact-transit-social-viber"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M21.365 1.554c-2.316-1.127-5.127-1.554-9.365-1.554-4.238 0-7.05.427-9.365 1.554C.913 2.385.034 4.09.006 6.315c-.052 4.1.353 7.82 2.668 10.957-.492 1.778-1.538 4.225-2.522 5.86-.345.57.172 1.272.795 1.054 2.42-1.077 5.564-2.83 7.158-3.663 1.2.3 2.515.427 3.895.427 4.238 0 7.05-.427 9.365-1.554C23.682 18.272 24 16.275 24 12c0-4.275-.318-6.272-2.635-7.446v-.001-.9-.1-.1l-.1-.2 M18.47 14.88c-.378.197-.84.288-1.385.275-.853-.016-1.745-.333-2.645-.945-1.637-1.11-2.92-2.37-3.957-3.91-.707-1.05-1.11-2.023-1.223-2.9-.064-.5.038-.97.3-1.4l.263-.44c.435-.724 1.135-.855 1.635-.453l1.196.963c.43.345.45.897.054 1.274l-.442.422c-.112.108-.13.262-.047.387.614 1.052 1.34 1.954 2.215 2.76.793.733 1.636 1.298 2.518 1.666.126.05.263.003.328-.1l.34-.482c.28-.403.793-.524 1.205-.27l1.314.808c.53.332.61.94.195 1.48L18.47 14.88z" />
                          </svg>
                        </a>
                        <a 
                          href="https://t.me/Cozzydine_Kyiv"
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-[#229ED9] hover:bg-[#229ED9] hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Contact on Telegram"
                          id="contact-transit-social-telegram"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701-.332 4.981c.488 0 .704-.224.977-.487l2.35-2.285 4.887 3.61c.901.497 1.55.241 1.775-.838l3.205-15.101c.329-1.316-.5-1.913-1.362-1.524z"/>
                          </svg>
                        </a>
                        <a 
                          href="https://www.instagram.com/cozzydine_kyiv?igsh=ZzZnamhndzgwbDF2&utm_source=qr" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-[#ee2a7b] hover:bg-gradient-to-tr hover:from-[#f9ce34] hover:via-[#ee2a7b] hover:to-[#6228d7] hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Follow on Instagram"
                          id="contact-transit-social-instagram"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                          </svg>
                        </a>
                        <a 
                          href="https://x.com/cozzydine" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-900 text-zinc-400 hover:bg-black hover:text-white transition-all duration-300 hover:scale-105 shadow-sm"
                          title="Follow on X"
                          id="contact-transit-social-x"
                        >
                          <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-8 border-b border-zinc-100 pb-5">
                    {[
                      { key: 'lybidska', label: t('tabLybidska') },
                      { key: 'bus', label: t('tabBus') },
                      { key: 'palats', label: t('tabPalats') },
                      { key: 'train', label: t('tabTrain') },
                      { key: 'centralBus', label: t('tabCentralBus') }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTransitTab(tab.key as any)}
                        className={`px-5 py-2.5 rounded-xl text-xs font-mono font-bold uppercase transition-all cursor-pointer ${
                          activeTransitTab === tab.key 
                            ? (theme === 'light' ? 'bg-zinc-950 text-white shadow-md' : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] text-[#FAFAFA] border-t border-[rgba(255,255,255,0.15)] shadow-md')
                            : (theme === 'light' ? 'bg-zinc-50 border border-zinc-200 text-zinc-500 hover:text-zinc-900' : 'bg-zinc-900/60 border border-zinc-800 text-zinc-400 hover:text-[#FAFAFA]')
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    <ol className="lg:col-span-7 space-y-4">
                      {(activeTransitTab === 'lybidska' ? tc.lybidskaSteps : activeTransitTab === 'bus' ? tc.busSteps : activeTransitTab === 'palats' ? tc.palatsSteps : activeTransitTab === 'centralBus' ? tc.centralBusSteps : tc.trainSteps).map((step, idx) => (
                        <li 
                          key={idx} 
                          className={`flex gap-4 items-start p-4 rounded-2xl mr-2 transition-all duration-300 ${
                            theme === 'light'
                              ? 'bg-zinc-50/75 border border-zinc-200 shadow-xs'
                              : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md'
                          }`}
                          style={theme === 'dark' ? { boxShadow: '0 8px 16px -4px rgba(0,0,0,0.4)' } : undefined}
                        >
                          <span className={`w-7 h-7 rounded-full text-xs font-mono font-black flex items-center justify-center shrink-0 ${
                            theme === 'light'
                              ? 'bg-emerald-50 border border-emerald-150 text-emerald-800'
                              : 'bg-emerald-950/40 border border-emerald-800/40 text-emerald-400'
                          }`}>
                            {idx + 1}
                          </span>
                          <p className={`text-sm sm:text-base leading-relaxed font-semibold text-left ${
                            theme === 'light' ? 'text-zinc-600' : 'text-[#FAFAFA]'
                          }`}>{step}</p>
                        </li>
                      ))}
                    </ol>

                    <div className={`transit-checklist-card lg:col-span-5 rounded-[24px] p-8 shadow-lg space-y-6 transition-all duration-300 ${
                      theme === 'light'
                        ? 'bg-gradient-to-br from-zinc-900 to-zinc-950 text-white'
                        : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.08)] text-white'
                    }`}>
                      <div className="flex items-center gap-2">
                        <Milestone className="w-5 h-5 text-emerald-500" />
                        <span className="text-xs font-mono font-bold uppercase tracking-wider transit-checklist-title">{t('checklistTitle')}</span>
                      </div>
                      <div className="space-y-4 font-mono text-xs text-zinc-300">
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpot2')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit250m') || '250 meters'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpotBus')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit300m') || '300 meters'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpotCentralBus')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit3m') || '3 mins ride'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpotRailway')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit10m') || '10 mins ride'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                          <span className="text-left">{t('checklistSpot3')}</span>
                          <span className="text-white font-black text-right shrink-0 ml-4">{t('lblTransit10m') || '10 mins ride'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

      </main>

      <React.Suspense fallback={null}>
        <PrivacyPolicyModal 
          isOpen={isPrivacyOpen}
          onClose={() => setIsPrivacyOpen(false)}
          theme={theme}
          currentLanguage={currentLanguage}
        />
      </React.Suspense>

      {selectedRoom && (
        <React.Suspense fallback={
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-zinc-950 p-8 rounded-3xl border border-zinc-800 text-center space-y-4 max-w-sm">
              <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-zinc-400 font-mono text-xs">Preparing booking engine...</p>
            </div>
          </div>
        }>
          <BookingModal 
            room={selectedRoom} 
            currentLanguage={currentLanguage} 
            onClose={() => { setSelectedRoom(null); setModalInitialStep(null); setModalInitialConfirmedBooking(null); }} 
            onSuccess={(booking) => {
              setBookingSuccess(true);
              setTimeout(() => {
                setBookingSuccess(false);
              }, 6000);
            }} 
            t={t} 
            initialCheckIn={barCheckIn}
            initialCheckOut={barCheckOut}
            initialGuests={barGuests}
            initialStep={modalInitialStep !== null ? modalInitialStep : undefined}
            initialConfirmedBooking={modalInitialConfirmedBooking !== null ? modalInitialConfirmedBooking : undefined}
          />
        </React.Suspense>
      )}
<footer className="cozzydine-footer bg-zinc-950 text-white border-t border-zinc-900 py-6 font-sans w-full">
  <div className="footer-container-grid max-w-7xl mx-auto px-6 grid grid-cols-3 items-center gap-4 w-full">
    <div className="footer-left-col flex flex-row items-center gap-3 justify-start min-w-0">
      <RobustImage 
        key={`logo-footer-${imageVersion}`}
        mediaKey="logo"
        localPaths={['/cozzydine_logo.png', '/gallery/rooms/cozzydine_logo.png', '/logo.png', '/gallery/rooms/logo.png']}
        fallbackUnsplashUrl={cozzydineLogoMain}
        alt="Cozzydine Logo"
        width="28"
        height="28"
        className="footer-logo-img w-7 h-7 rounded-full border border-zinc-800 shrink-0"
        loading="lazy"
      />
      <div className="flex flex-col items-start text-left min-w-0">
        <span className="footer-brand-title font-display font-black text-white tracking-widest text-[10px] uppercase">Cozzydine Kyiv</span>
        <p className="footer-address-text text-[9.5px] text-zinc-300 font-sans mt-0.5 font-normal tracking-wide text-left whitespace-nowrap">
          {physicalAddress} &bull; {phoneNumber}
        </p>
      </div>
    </div>

    <div className="footer-center-col flex justify-center items-center text-center min-w-0">
      <button
        type="button"
        onClick={() => setIsPrivacyOpen(true)}
        className="footer-privacy-btn inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-full border border-white bg-zinc-900 text-white hover:bg-white hover:text-zinc-950 text-xs font-sans font-medium transition-all duration-200 cursor-pointer w-auto shadow-sm shrink-0"
      >
        <Shield className="footer-privacy-icon w-3.5 h-3.5 text-white hover:text-inherit shrink-0" />
        <span className="footer-privacy-text text-white text-xs whitespace-nowrap">{t('lblPrivacyPolicy') || 'Privacy Policy'}</span>
      </button>
    </div>

    <div className="footer-right-col flex justify-end items-center text-right min-w-0">
      <p className="footer-copyright-text text-[10px] text-zinc-300 font-sans font-normal text-right whitespace-nowrap">
        &copy; {new Date().getFullYear()} Cozzydine. {t('lblAllRightsReserved') || 'All rights reserved.'}
      </p>
    </div>
  </div>
</footer>

      {isMediaCabinetOpen && (
        <React.Suspense fallback={null}>
          <MediaManager 
            isOpen={isMediaCabinetOpen}
            onClose={() => setIsMediaCabinetOpen(false)}
            onUploadSuccess={() => setImageVersion(Date.now().toString())}
            imageVersion={imageVersion}
          />
        </React.Suspense>
      )}

    </div>
    </div>

    <div id="cozzy-bot-launcher" className="fixed bottom-6 right-6 z-[99999]">
      <React.Suspense fallback={null}>
        <ConciergeBot currentLanguage={currentLanguage} />
      </React.Suspense>
    </div>
    </>
  );
}
